import React from 'react';
import { 
  Box, Button, HStack, IconButton, Image, Input, Modal, ModalOverlay, ModalContent, 
  ModalHeader, ModalCloseButton, ModalBody, ModalFooter, FormControl, FormLabel, FormHelperText,
  useToast, VStack, Text, SimpleGrid, useColorModeValue, useDisclosure,
  Textarea, Icon, useBreakpointValue, InputGroup, InputLeftElement,
  ButtonGroup, Spinner, Heading, Td, Th, Thead, Tr, Tbody, Table, Switch,
  Select, Badge, Tabs, TabList, TabPanels, Tab, TabPanel, Accordion, AccordionItem,
  AccordionButton, AccordionPanel, AccordionIcon, AspectRatio, Link, Alert, AlertIcon, Checkbox
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus, FiSearch, FiUpload, FiExternalLink, FiVideo, FiX, FiRefreshCcw, FiLink, FiGlobe } from 'react-icons/fi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import AdminLayout from '../../layouts/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Article, deleteArticle, getArticles, createArticle, updateArticle, uploadFile, CreateArticlePayload, UpdateArticlePayload, getArticleMatchLink, putArticleMatchLink, deleteArticleMatchLink } from '../../services/articles';
import { generateBlogAI, generateMainImageAI } from '../../services/ai';
import { useState, useRef, useCallback, useMemo } from 'react';
import DOMPurify from 'dompurify';
import RichTextEditor from '../../components/common/RichTextEditor';
import { assetUrl } from '../../utils/url';
import { getPublicSettings } from '../../services/settings';
import { getZoneramaManifestWithFallbacks, getZoneramaAlbum, putZoneramaPick, saveAlbumToCache } from '../../services/zonerama';
import { facrApi } from '../../services/facr/facrApi';
import { API_URL } from '../../services/api';
import { triggerPrefetch } from '../../services/admin/prefetch';
import { saveArticleReliable } from '../../services/articleSave';
import AlbumPhotoPicker from '../../components/admin/AlbumPhotoPicker';
import PollLinker from '../../components/admin/PollLinker';
import ThumbnailPreview from '../../components/common/ThumbnailPreview';
import FilePreview from '../../components/common/FilePreview';
import SaveStatusIndicator from '../../components/common/SaveStatusIndicator';
import AILoadingModal from '../../components/common/AILoadingModal';
import UploadPanel, { UploadPanelFile } from '../../components/common/UploadPanel';
 
import { useAutoSave, loadDraft, getDraftMetadata } from '../../hooks/useAutoSave';

import { getCachedYouTube, YouTubeVideo } from '../../services/youtube';
import { createShortLink } from '../../services/shortlinks';
import AIPromptInput from '../../components/common/AIPromptInput';
import { AITextModelId, getDefaultTextModelId } from '../../utils/aiModels';
import { processAIContent, cleanAIText } from '../../utils/aiTextProcessing';
import { getCompetitionAliasesPublic } from '../../services/competitionAliases';
import { BlogTranslator } from '../../components/admin/BlogTranslator';
import { translateBlogContent, detectLanguage } from '../../services/translation';

// Inline small component to show match link badge in list (with short label)
const MatchLinkBadge: React.FC<{ articleId: number }> = ({ articleId }) => {
  const linkQ = useQuery({
    queryKey: ['article-match-link', articleId],
    queryFn: () => getArticleMatchLink(articleId),
    enabled: typeof articleId !== 'undefined' && articleId !== null && (typeof articleId === 'number' ? articleId > 0 : String(articleId).trim() !== ''),
    staleTime: 60_000,
    retry: false,
  });
  
  const mid = (linkQ.data as any)?.external_match_id;

  const facrQ = useQuery({
    queryKey: ['facr-cached-match', mid],
    enabled: !!mid,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      try {
        const origin = new URL(API_URL, typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000').origin;
        const url = `${origin}/cache/prefetch/facr_club_info.json`;
        const res = await fetch(url, { cache: 'no-cache' });
        if (!res.ok) return null;
        const json = await res.json();
        const comps = Array.isArray(json?.competitions) ? json.competitions : [];
        for (const c of comps) {
          const matches = Array.isArray(c.matches) ? c.matches : [];
          for (const m of matches) {
            const id = String(m.match_id || m.id);
            if (id === String(mid)) return { ...m, competitionName: c.name };
          }
        }
        return null;
      } catch (error) {
        console.error('Failed to fetch FACR match data:', error);
        return null;
      }
    }
  });

  // Show loading state while fetching (after hooks are declared to keep order consistent)
  if (linkQ.isLoading) {
    return <Badge colorScheme="gray">Načítání...</Badge>;
  }
  
  if (!mid) return <Badge colorScheme="gray">Nepropojeno</Badge>;

  // Guard against errors
  if (facrQ.isError || linkQ.isError) {
    return <Badge colorScheme="red">Chyba načítání</Badge>;
  }

  const m: any = facrQ.data;
  const scoreText = m ? (m.score || (m.result_home!=null && m.result_away!=null ? `${m.result_home}:${m.result_away}` : 'vs')) : '';
  const hasScore = !!m && !!scoreText && scoreText !== 'vs';
  const color = hasScore ? 'green' : 'yellow';
  const label = m
    ? `${String(m.home || m.home_team || '')} ${String(scoreText)} ${String(m.away || m.away_team || '')}`
    : `ID: ${String(mid)}`;
  const linkHrefRaw = (m && (m.facr_link || m.report_url)) ? String(m.facr_link || m.report_url) : '';
  const normalizeFacrLink = (href: string): string => {
    try {
      const u = new URL(href, typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000');
      if (u.hostname === 'is.fotbal.cz') {
        u.hostname = 'www.fotbal.cz';
      }
      return u.toString();
    } catch { return href; }
  };
  const linkHref = linkHrefRaw ? normalizeFacrLink(linkHrefRaw) : '';
  return (
    <HStack spacing={2}>
      <Badge colorScheme={color as any} title={m?.competitionName ? String(m.competitionName) : undefined}>Zápas: {label}</Badge>
      {linkHref ? (
        <IconButton
          aria-label="Otevřít zápas na fotbal.cz"
          size="xs"
          as="a"
          href={linkHref}
          target="_blank"
          rel="noopener noreferrer"
          icon={<FiExternalLink />}
          variant="ghost"
        />
      ) : null}
    </HStack>
  );
};
// Note: Image resize module removed due to incompatibility with Quill v2

const PAGE_SIZE = 10;

const inputBg = 'white'; // Temporary placeholder for dark mode support

const parseYoutubeVideoId = (raw: string): string => {
  const value = String(raw || '').trim();
  if (!value) return '';

  // Try to parse as URL first
  try {
    const url = new URL(value.startsWith('http') ? value : `https://${value}`);
    if (url.hostname.includes('youtu.be')) {
      const parts = url.pathname.split('/').filter(Boolean);
      if (parts.length > 0) {
        return parts[0];
      }
    }
    if (url.pathname.includes('/shorts/')) {
      const parts = url.pathname.split('/shorts/');
      if (parts.length > 1) {
        const idCandidate = parts[1].split(/[?&]/)[0];
        if (/^[0-9A-Za-z_-]{11}$/.test(idCandidate)) {
          return idCandidate;
        }
      }
    }
    const vParam = url.searchParams.get('v');
    if (vParam && /^[0-9A-Za-z_-]{11}$/.test(vParam)) {
      return vParam;
    }
  } catch {
    // ignore and fall back to regex parsing
  }

  const regex = /(?:v=|\/)([0-9A-Za-z_-]{11})(?![0-9A-Za-z_-])/;
  const match = value.match(regex);
  if (match && match[1]) {
    return match[1];
  }

  if (/^[0-9A-Za-z_-]{11}$/.test(value)) {
    return value;
  }

  return '';
};

const makeSlug = (text: string): string => {
  return String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^a-z0-9\s-]/g, '') // Keep only letters, numbers, spaces, hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

const ArticlesAdminPage = () => {
  const { user } = useAuth();
  const isAdmin = (user as any)?.role === 'admin';
  const toast = useToast();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  interface EditingArticle extends Partial<Article> {
    slug?: string;
    slugModified?: boolean;
    // category is inherited from Article, no need to redefine
    category_id?: number;
    category_name?: string;
  }

  const [editing, setEditing] = useState<EditingArticle | null>(null);
  const [showDraftRecovery, setShowDraftRecovery] = useState(false);
  const [draftKey, setDraftKey] = useState<string>('');
  const [localDraft, setLocalDraft] = useState<EditingArticle | null>(null);
  const [manualSaveInProgress, setManualSaveInProgress] = useState(false);
  const [lastContentChangeAt, setLastContentChangeAt] = useState<number | null>(null);
  const [isContentTyping, setIsContentTyping] = useState(false);

  // Load cached matches list for quick picker (from prefetch cache like MatchesAdminPage)
  React.useEffect(() => {
    (async () => {
      try {
        const origin = new URL(API_URL, typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000').origin;
        const url = `${origin}/cache/prefetch/facr_club_info.json`;
        const res = await fetch(url, { headers: { 'Cache-Control': 'no-cache' } });
        if (!res.ok) return;
        const json = await res.json();
        const comps = Array.isArray(json?.competitions) ? json.competitions : [];
        const items: any[] = comps.flatMap((c: any) => (Array.isArray(c.matches) ? c.matches : []).map((m: any) => {
          const score = m.score || (m.result_home!=null&&m.result_away!=null?`${m.result_home}:${m.result_away}`:'vs');
          return {
            id: String(m.match_id || m.id || ''),
            date: m.date_time || m.date || '',
            label: `${m.date_time || m.date || ''} • ${m.home || m.home_team || ''} ${score} ${m.away || m.away_team || ''} ${c?.name ? '('+c.name+')' : ''}`.trim(),
            competition: c?.name || '',
            competition_code: c?.code || c?.id || '',
            home: m.home || m.home_team || '',
            away: m.away || m.away_team || '',
            score: score
          };
        }));
        // keep latest 200 for performance
        setMatchOptions(items.slice(-200).reverse());
      } catch { /* ignore */ }
    })();
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-articles', { page, page_size: PAGE_SIZE, q: search, published: false }],
    // Important: pass published=false to fetch ALL (backend defaults to only published when param missing)
    queryFn: () => getArticles({ page, page_size: PAGE_SIZE, published: false, q: search }),
    keepPreviousData: true,
  });

  // Settings for logo fetch (for placeholder image)
  const settingsQ = useQuery({
    queryKey: ['public-settings'],
    queryFn: getPublicSettings,
    staleTime: 5 * 60_000,
  });

  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiAudience, setAiAudience] = useState('Fanoušci klubu');
  const [aiMinWords, setAiMinWords] = useState<number>(500);
  const [aiMinWordsInput, setAiMinWordsInput] = useState<string>('500');
  const [aiProgress, setAiProgress] = useState<number | undefined>(undefined);
  const [aiStartTime, setAiStartTime] = useState<number | null>(null);
  const [aiModelId, setAiModelId] = useState<AITextModelId>(getDefaultTextModelId());
  const [featSwitchLoading, setFeatSwitchLoading] = useState<boolean>(false);
  const [competitions, setCompetitions] = useState<Array<{ code?: string; name: string }>>([]);
  const [aliasesMap, setAliasesMap] = useState<Record<string, string>>({});
  const [aliasesList, setAliasesList] = useState<Array<{ code: string; alias: string; original_name?: string }>>([]);
  // Match link state
  const [linkedMatchId, setLinkedMatchId] = useState<string>('');
  const [linkedMatchTitle, setLinkedMatchTitle] = useState<string>('');
  const [matchIdInput, setMatchIdInput] = useState<string>('');
  const [matchOptions, setMatchOptions] = useState<Array<{ id: string; label: string; date?: string; competition?: string; home?: string; away?: string; score?: string }>>([]);
  const [matchSearch, setMatchSearch] = useState('');
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [matchDateFilter, setMatchDateFilter] = useState<string>('');
  const [tempMatchLink, setTempMatchLink] = useState<string>(''); // Temporary storage for new articles

  // Zonerama picker state
  const [zAlbumLink, setZAlbumLink] = useState<string>('');
  const [zAlbumPhotos, setZAlbumPhotos] = useState<Array<{ id: string; page_url: string; image_1500?: string; title?: string }>>([]);
  const [zLoading, setZLoading] = useState<boolean>(false);
  const [albumPickerOpen, setAlbumPickerOpen] = useState<boolean>(false);
  const { isOpen: isAlbumPickerOpen, onOpen: onAlbumPickerOpen, onClose: onAlbumPickerClose } = useDisclosure();
  const { isOpen: isGalleryPickerOpen, onOpen: onGalleryPickerOpen, onClose: onGalleryPickerClose } = useDisclosure();
  const [cachedAlbums, setCachedAlbums] = useState<Array<{ id: string; date: string; title?: string; photos: Array<{ id: string; image_1500: string; page_url: string }> }>>([]);
  const [galleryLoading, setGalleryLoading] = useState<boolean>(false);
  const [galleryExpandedAlbumId, setGalleryExpandedAlbumId] = useState<string | null>(null);
  const [galleryVisibleCount, setGalleryVisibleCount] = useState<number>(48);
  const [youtubeVideos, setYoutubeVideos] = useState<YouTubeVideo[]>([]);
  const [youtubeLoading, setYoutubeLoading] = useState<boolean>(false);
  const [youtubeSearch, setYoutubeSearch] = useState<string>('');
  const [youtubeManualInput, setYoutubeManualInput] = useState<string>('');
  const { isOpen: isYouTubeModalOpen, onOpen: onYouTubeModalOpen, onClose: onYouTubeModalClose } = useDisclosure();
  const { isOpen: isExistingAlbumsOpen, onOpen: onExistingAlbumsOpen, onClose: onExistingAlbumsClose } = useDisclosure();
  const [zVisibleCount, setZVisibleCount] = useState<number>(24);
  const [existingSelectedAlbum, setExistingSelectedAlbum] = useState<{ id: string; date: string; title?: string; photos: Array<{ id: string; image_1500: string; page_url: string }> } | null>(null);
  const [existingSelectedPhotos, setExistingSelectedPhotos] = useState<Set<string>>(new Set());
  const [existingVisibleCount, setExistingVisibleCount] = useState<number>(48);
  const [mainImageLoading, setMainImageLoading] = useState<boolean>(false);

  // Track when the user is actively typing in the rich text editor
  React.useEffect(() => {
    if (lastContentChangeAt == null) {
      setIsContentTyping(false);
      return;
    }
    setIsContentTyping(true);
    const timeout = window.setTimeout(() => {
      if (lastContentChangeAt && Date.now() - lastContentChangeAt >= 800) {
        setIsContentTyping(false);
      }
    }, 800);
    return () => window.clearTimeout(timeout);
  }, [lastContentChangeAt]);

  // Auto-save hook - saves draft automatically
  const autosaveData = React.useMemo(() => {
    if (!editing) return {} as any;
    // Attach a synthetic timestamp so that enabling/disabling does not skip the last change
    return { ...(editing as any), __autosaveTs: lastContentChangeAt || 0 } as any;
  }, [editing, lastContentChangeAt]);

  const { saveStatus, lastSaved, forceSave, clearDraft } = useAutoSave({
    data: autosaveData,
    storageKey: draftKey,
    onSave: async (data) => {
      // Use centralized reliable saver (normalizes payload, retries, triggers cache refresh when published)
      if ((data as any)?.id || (data as any)?.title?.trim()) {
        const saved: any = await saveArticleReliable(data as any);
        if (saved?.id && !(data as any)?.id) {
          setEditing(prev => ({ ...(prev as any), id: saved.id } as any));
          setDraftKey(`draft-article-${saved.id}`);
          try { localStorage.removeItem('draft-article-new'); } catch {}
        }
        return saved;
      }
      return {};
    },
    debounceMs: 2000,
    enabled: isOpen && editing !== null && !manualSaveInProgress && !isContentTyping,
  });

  // Load local new-draft and expose in list (no popup)
  const refreshLocalDraft = React.useCallback(() => {
    try {
      const key = 'draft-article-new';
      const metadata = getDraftMetadata(key);
      if (metadata && metadata.age < 1440) {
        const d = loadDraft<EditingArticle>(key);
        if (d) {
          const restored: any = { ...d };
          if (restored.id) delete restored.id;
          setLocalDraft(restored);
          return;
        }
      }
    } catch {}
    setLocalDraft(null);
  }, []);

  React.useEffect(() => {
    refreshLocalDraft();
  }, [refreshLocalDraft]);

  // Fetch cached Zonerama gallery from prefetch
  const fetchCachedGallery = useCallback(async () => {
    try {
      setGalleryLoading(true);
      // Resolve backend origin for cached files regardless of frontend origin
      let origin = '';
      try {
        const assetBase = (process.env.REACT_APP_ASSET_BASE_URL || '').trim();
        if (assetBase) {
          origin = new URL(assetBase, typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000').origin;
        }
      } catch {}
      if (!origin) {
        try {
          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';
          origin = new URL(apiUrl, typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000').origin;
        } catch {}
      }
      const profileUrl = `${origin}/cache/prefetch/zonerama_profile.json`;
      const albumsUrl = `${origin}/cache/prefetch/zonerama_albums.json`;

      const [profileRes, albumsRes] = await Promise.allSettled([
        fetch(profileUrl, { cache: 'no-cache' }),
        fetch(albumsUrl, { cache: 'no-cache' }),
      ]);

      let albums: any[] = [];
      if (albumsRes.status === 'fulfilled' && albumsRes.value.ok) {
        const json = await albumsRes.value.json();
        albums = Array.isArray(json) ? json : [];
        if (Array.isArray(albums) && albums.length > 0) {
          const withPhotos = albums.filter((a: any) => Array.isArray(a?.photos) && a.photos.length > 0);
          albums = withPhotos.length > 0 ? withPhotos : [];
        }
      }
      // Legacy fallback: some caches might have { albums: [...] }
      if (albums.length === 0 && profileRes.status === 'fulfilled' && profileRes.value.ok) {
        const p = await profileRes.value.json();
        const arr = Array.isArray(p?.albums) ? p.albums : [];
        // Only keep entries that already include photos
        albums = arr.filter((a: any) => Array.isArray(a.photos) && a.photos.length > 0);
      }

      // Final fallback: synthesize from flat manifest/picks
      if (albums.length === 0) {
        try {
          const items = await getZoneramaManifestWithFallbacks();
          if (Array.isArray(items) && items.length > 0) {
            const byAlbum: Record<string, any[]> = {} as any;
            items.forEach((it: any) => {
              const aid = String(it.album_id || 'unknown');
              (byAlbum[aid] = byAlbum[aid] || []).push(it);
            });
            albums = Object.entries(byAlbum).map(([aid, arr]) => ({
              id: aid,
              title: 'Album',
              url: (arr[0] as any).page_url || '#',
              date: '',
              photos_count: (arr as any[]).length,
              photos: (arr as any[]).slice(0, 100).map((p: any) => ({ id: String(p.id || ''), image_1500: String(p.src || p.local || ''), page_url: String(p.page_url || '') })),
            }));
          }
        } catch { /* ignore */ }
      }

      // Normalize albums to ensure photos is always an array with required fields
      albums = albums.map((a: any) => {
        const p = Array.isArray(a.photos) ? a.photos : [];
        const normPhotos = p.map((ph: any) => ({
          id: String(ph.id || ''),
          image_1500: String(ph.image_1500 || ph.src || ph.local || ''),
          page_url: String(ph.page_url || ''),
        }));
        return { ...a, photos: normPhotos };
      });

      // Sort by date (desc). Support both ISO (yyyy-mm-dd) and Czech (dd.mm.yyyy)
      const parseDate = (s: string): number => {
        const str = String(s || '').trim();
        if (!str) return 0;
        const iso = Date.parse(str);
        if (!Number.isNaN(iso)) return iso;
        const m = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
        if (m) {
          const d = parseInt(m[1], 10);
          const mo = parseInt(m[2], 10) - 1;
          const y = parseInt(m[3], 10);
          return new Date(y, mo, d).getTime();
        }
        return 0;
      };
      const sorted = [...albums].sort((a, b) => parseDate(b.date) - parseDate(a.date));
      setCachedAlbums(sorted);
      if (sorted.length === 0) {
        toast({ title: 'Žádné alba nenalezena', description: 'Cache galerie je prázdná nebo neobsahuje fotografie.', status: 'info', duration: 4000 });
      }
    } catch (e: any) {
      toast({ title: 'Načtení galerie selhalo', description: e?.message || 'Zkuste to prosím znovu.', status: 'error' });
    } finally {
      setGalleryLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remove toast from dependencies to prevent infinite loops
  const fetchYouTubeVideos = useCallback(async () => {
    try {
      setYoutubeLoading(true);
      const payload = await getCachedYouTube();
      let videos: YouTubeVideo[] = [];
      if (payload && Array.isArray(payload.videos)) {
        videos = payload.videos;
      }
      setYoutubeVideos(videos);
      if (!payload || videos.length === 0) {
        toast({ title: 'YouTube videa nenalezena', description: 'Zkontrolujte nastavení YouTube kanálu nebo zkuste obnovit cache.', status: 'info', duration: 4000 });
      }
    } catch (e: any) {
      toast({ title: 'Načtení YouTube videí selhalo', description: e?.message || 'Zkuste to prosím znovu.', status: 'error' });
    } finally {
      setYoutubeLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (isYouTubeModalOpen && youtubeVideos.length === 0 && !youtubeLoading) {
      fetchYouTubeVideos();
    }
  }, [isYouTubeModalOpen, youtubeVideos.length, youtubeLoading, fetchYouTubeVideos]);

  React.useEffect(() => {
    if (isGalleryPickerOpen && cachedAlbums.length === 0 && !galleryLoading) {
      fetchCachedGallery();
    }
  }, [isGalleryPickerOpen, cachedAlbums.length, galleryLoading, fetchCachedGallery]);

  React.useEffect(() => {
    if (isExistingAlbumsOpen && cachedAlbums.length === 0 && !galleryLoading) {
      fetchCachedGallery();
    }
  }, [isExistingAlbumsOpen, cachedAlbums.length, galleryLoading, fetchCachedGallery]);

  const filteredYoutubeVideos = useMemo(() => {
    const q = youtubeSearch.trim().toLowerCase();
    if (!q) return youtubeVideos;
    return youtubeVideos.filter((video) => {
      const title = (video.title || '').toLowerCase();
      return title.includes(q) || String(video.video_id || '').toLowerCase().includes(q);
    });
  }, [youtubeVideos, youtubeSearch]);

  const handleSelectYoutubeVideo = useCallback((video: YouTubeVideo) => {
    setEditing((prev) => ({
      ...(prev as any),
      youtube_video_id: video.video_id,
      youtube_video_title: video.title,
      youtube_video_url: `https://www.youtube.com/watch?v=${video.video_id}`,
      youtube_video_thumbnail: video.thumbnail_url,
    }));
    toast({ title: 'Video přidáno k článku', description: video.title, status: 'success' });
    onYouTubeModalClose();
  }, [toast, onYouTubeModalClose]);

  const handleClearYoutubeVideo = useCallback(() => {
    setEditing((prev) => ({
      ...(prev as any),
      youtube_video_id: '',
      youtube_video_title: '',
      youtube_video_url: '',
      youtube_video_thumbnail: '',
    }));
    toast({ title: 'Video odstraněno', status: 'info' });
  }, [toast]);

  const handleGenerateMainImage = useCallback(async () => {
    if (!editing || !editing.title || !editing.title.trim()) {
      toast({ title: 'Chybí titulek článku', description: 'Nejprve zadejte titulek, aby AI věděla, co má zobrazit.', status: 'warning', duration: 4000 });
      return;
    }
    try {
      setMainImageLoading(true);
      const categoryName = (editing as any)?.category_name || (editing as any)?.category?.name || '';
      const clubName = (settingsQ.data as any)?.club_name || '';
      const primaryColor = (settingsQ.data as any)?.primary_color || '';
      const secondaryColor = (settingsQ.data as any)?.secondary_color || '';
      const resp = await generateMainImageAI({
        subject: 'article',
        title: editing.title,
        category: categoryName,
        club_name: clubName,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
      });
      if (!resp || !resp.url) {
        toast({ title: 'AI nevrátila obrázek', description: 'Zkuste to prosím znovu nebo nastavte obrázek ručně.', status: 'warning', duration: 5000 });
        return;
      }
      setEditing((prev) => ({ ...(prev as any), image_url: resp.url } as any));
      toast({ title: 'Titulní obrázek vygenerován', description: 'Obrázek byl nastaven k článku.', status: 'success', duration: 4000 });
    } catch (e: any) {
      toast({ title: 'Generování obrázku selhalo', description: e?.response?.data?.error || e?.message || 'Zkuste to prosím znovu.', status: 'error', duration: 5000 });
    } finally {
      setMainImageLoading(false);
    }
  }, [editing, toast]);

  const handleManualYoutubeSubmit = useCallback(() => {
    const id = parseYoutubeVideoId(youtubeManualInput);
    if (!id) {
      toast({ title: 'Neplatný YouTube odkaz', description: 'Zadejte prosím platné ID nebo odkaz na video.', status: 'warning' });
      return;
    }
    const existing = youtubeVideos.find((v) => v.video_id === id);
    const title = existing?.title || 'YouTube video';
    const thumbnail = existing?.thumbnail_url || `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
    setEditing((prev) => ({
      ...(prev as any),
      youtube_video_id: id,
      youtube_video_title: title,
      youtube_video_url: `https://www.youtube.com/watch?v=${id}`,
      youtube_video_thumbnail: thumbnail,
    }));
    toast({ title: 'Video nastaveno', description: title, status: 'success' });
    setYoutubeManualInput('');
  }, [youtubeManualInput, youtubeVideos, toast]);

  const pickZoneramaImage = useCallback(async (pick: { id: string; album_id?: string; album_url: string; page_url?: string; image_url: string; title?: string }) => {
    try {
      // Set cover image immediately and link the album to the article as well
      setEditing((prev) => {
        const current = (prev as any) || {};
        
        // Try to derive numeric album ID from URL when not provided
        let aid = String(pick.album_id || '').trim();
        if (!aid && pick.album_url) {
          try {
            const m = pick.album_url.match(/\/Album\/(\d+)/i);
            if (m && m[1]) aid = m[1];
          } catch {}
        }
        
        // Only update gallery_album_url if it's not already set (preserve existing gallery connection)
        const shouldUpdateAlbumUrl = !current.gallery_album_url;
        
        return {
          ...current,
          image_url: pick.image_url,
          gallery_album_url: shouldUpdateAlbumUrl ? pick.album_url : current.gallery_album_url,
          ...(aid ? { gallery_album_id: aid } : {}),
        };
      });
      // Persist pick to unified cache (admin only)
      if (isAdmin) {
        await putZoneramaPick({
          id: pick.id,
          album_id: pick.album_id,
          album_url: pick.album_url,
          page_url: pick.page_url,
          image_url: pick.image_url,
          title: pick.title,
        } as any);
        toast({ title: 'Obrázek vybrán ze Zonerama', description: 'Album bylo také propojeno s článkem.', status: 'success' });
      } else {
        toast({ title: 'Obrázek nastaven', description: 'Album bylo také propojeno s článkem.', status: 'success' });
      }
    } catch (e: any) {
      toast({ title: 'Uložení výběru selhalo', description: e?.response?.data?.error || e?.message || 'Chyba', status: 'error' });
    }
  }, [toast]);

  // Handle album photo selection for blog content
  const handleAlbumPhotosSelected = useCallback(async (photos: Array<{ id: string; page_url: string; image_1500: string }>, albumInfo: any) => {
    try {
      // Check if this is selection from existing gallery (no URL means it's from cache)
      const isFromExistingGallery = !albumInfo?.url;
      
      // Save album to cache (admins only) only when adding new album via URL
      if (isAdmin && albumInfo?.url && !isFromExistingGallery) {
        toast({ title: 'Ukládám album...', status: 'info', duration: 2000 });
        const limit = Math.max(500, Number(albumInfo?.photos_count || 0) || photos.length || 100);
        await saveAlbumToCache(albumInfo.url, limit);
      }
      
      // Store album info with article and append images to content
      setEditing((prev) => {
        const currentContent = (prev as any)?.content || '';
        const photosHTML = photos
          .map(p => `<img src="${p.image_1500}" alt="Gallery photo" data-page-url="${p.page_url}" data-img-id="${p.id}" />`)
          .join('\n');
        
        // For existing gallery, preserve the existing gallery_album_url and don't replace existing album data
        const finalAlbumUrl = isFromExistingGallery 
          ? (prev as any)?.gallery_album_url  // Keep existing URL
          : albumInfo.url;                   // Use new album URL
        
        // Only update album info if it's a new album or if no album exists
        const shouldUpdateAlbumInfo = !isFromExistingGallery || !(prev as any)?.gallery_album_id;
        
        const updates: any = {
          content: currentContent + '\n' + photosHTML,
        };
        
        // Only update album info for new albums or when no album exists
        if (shouldUpdateAlbumInfo) {
          updates.gallery_album_id = albumInfo.id;
          updates.gallery_album_url = finalAlbumUrl;
          updates.gallery_photo_ids = photos.map(p => p.id);
        }
        
        return {
          ...(prev as any),
          ...updates,
        };
      });
      
      // Only populate album in Media tab when adding NEW album (not from existing gallery)
      if (!isFromExistingGallery) {
        // REUSE ALBUM: Also populate the album in Media tab so it doesn't need to be fetched twice
        // Map album photos to the format used in zAlbumPhotos state
        const mappedPhotos = albumInfo.photos?.map((p: any) => ({
          id: p.id,
          page_url: p.page_url,
          image_1500: p.image_1500 || '',
          title: p.title || '',
        })) || photos.map(p => ({
          id: p.id,
          page_url: p.page_url,
          image_1500: p.image_1500 || '',
          title: '',
        }));
        
        setZAlbumLink(albumInfo.url || '');
        setZAlbumPhotos(mappedPhotos);
      }

      toast({ 
        title: isFromExistingGallery ? 'Fotografie propojeny s článkem' : 'Album přidáno', 
        description: `${photos.length} fotografií vloženo do článku.`,
        status: 'success',
        duration: 4000 
      });
    } catch (error: any) {
      toast({
        title: 'Chyba',
        description: error.message || 'Nepodařilo se přidat album',
        status: 'error',
        duration: 5000,
      });
    }
  }, [toast]);

  const fetchAlbumByLink = useCallback(async () => {
    const link = zAlbumLink.trim();
    if (!link) return;
    try {
      setZLoading(true);
      // Use correct API endpoint format based on album-api.md
      const params = new URLSearchParams({
        link: link,
        photo_limit: '24',
        rendered: 'true'
      });
      const res = await fetch(`${API_URL}/zonerama-album?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch album');
      const data = await res.json();
      
      // Handle the response structure from API - it can be:
      // 1. { albums: [{ photos: [...] }] } - new format
      // 2. { album: {...}, photos: [...] } - direct format
      let photos: any[] = [];
      
      if (Array.isArray(data?.albums) && data.albums.length > 0) {
        // New format with albums array
        photos = data.albums[0]?.photos || [];
      } else if (Array.isArray(data?.photos)) {
        // Direct format with photos array
        photos = data.photos;
      }
      
      if (photos.length === 0) {
        toast({ title: 'Album načteno, ale neobsahuje žádné fotografie', status: 'info' });
      }
      
      setZAlbumPhotos(photos);
    } catch (e: any) {
      toast({ title: 'Načtení alba selhalo', description: e?.message || 'Chyba', status: 'error' });
    } finally {
      setZLoading(false);
    }
  }, [zAlbumLink, toast]);

  // Match link is now included in article data, no need to fetch separately
  // The openEdit function handles setting it from the article data

  const filteredMatchOptions = useMemo(() => {
    let opts = matchOptions;

    const normalize = (s?: string) => String(s || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    const toAlias = (compName?: string): string => {
      const n = normalize(compName);
      for (const a of aliasesList) {
        const aAlias = normalize(a.alias);
        const aOrig = normalize(a.original_name || '');
        if ((aOrig && (n === aOrig || n.includes(aOrig) || aOrig.includes(n))) ||
            (aAlias && (n === aAlias || n.includes(aAlias) || aAlias.includes(n)))) {
          return a.alias;
        }
      }
      return String(compName || '');
    };

    const catRaw = String((editing as any)?.category_name || '').trim();
    const cat = normalize(catRaw);
    if (cat) {
      const selectedCodes = new Set<string>();
      for (const a of aliasesList) {
        const aAlias = normalize(a.alias);
        const aOrig = normalize(a.original_name || '');
        if ((aAlias && aAlias === cat) || (aOrig && aOrig === cat)) {
          selectedCodes.add(a.code);
        }
      }

      opts = opts.filter(o => {
        const compName = String(o.competition || '');
        const compNorm = normalize(compName);
        const compAlias = normalize(toAlias(compName));
        const code = String((o as any).competition_code || '');
        const codeMatch = selectedCodes.size > 0 && code ? selectedCodes.has(code) : false;
        return codeMatch || compNorm === cat || compAlias === cat || compNorm.includes(cat) || compAlias.includes(cat);
      });
    }

    const q = matchSearch.trim().toLowerCase();
    if (q) {
      opts = opts.filter(o => o.label.toLowerCase().includes(q));
    }

    if (matchDateFilter) {
      opts = opts.filter(o => {
        const dateStr = o.date || '';
        return dateStr.includes(matchDateFilter);
      });
    }
    const now = Date.now();
    const parseTime = (s?: string): number => {
      if (!s) return Number.MAX_SAFE_INTEGER;
      const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2}))?/);
      if (m) {
        const d = parseInt(m[1], 10);
        const mo = parseInt(m[2], 10) - 1;
        const y = parseInt(m[3], 10);
        const h = m[4] ? parseInt(m[4], 10) : 0;
        const mi = m[5] ? parseInt(m[5], 10) : 0;
        return new Date(y, mo, d, h, mi).getTime();
      }
      const t = Date.parse(s);
      return isNaN(t) ? Number.MAX_SAFE_INTEGER : t;
    };
    opts = opts.sort((a, b) => {
      const ta = parseTime(a.date);
      const tb = parseTime(b.date);
      const da = ta - now;
      const db = tb - now;
      const aUpcoming = da >= 0;
      const bUpcoming = db >= 0;
      if (aUpcoming !== bUpcoming) return aUpcoming ? -1 : 1;
      if (aUpcoming) return da - db;
      return Math.abs(da) - Math.abs(db);
    });

    return opts;
  }, [matchOptions, matchSearch, matchDateFilter, (editing as any)?.category_name, aliasesList]);

  // Load club competitions + aliases for quick category pick
  React.useEffect(() => {
    (async () => {
      try {
        const settings = await getPublicSettings();
        const clubId = (settings as any)?.club_id || '';
        const clubType = ((settings as any)?.club_type || 'football') as 'football' | 'futsal';

        // Aliases
        let amap: Record<string, string> = {};
        try {
          const list = await getCompetitionAliasesPublic();
          list.forEach((a) => { if (a.code && a.alias) amap[a.code] = a.alias; });
          setAliasesList(list as any);
        } catch {}

        // Try cached prefetch JSON first
        let comps: Array<{ code?: string; name: string }> = [];
        try {
          const origin = new URL(API_URL, typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000').origin;
          const res = await fetch(`${origin}/cache/prefetch/facr_club_info.json`, { cache: 'no-cache' });
          if (res.ok) {
            const json = await res.json();
            const arr = Array.isArray((json as any)?.competitions) ? (json as any).competitions : [];
            comps = arr.map((c: any) => ({ code: c.code || c.id, name: c.name || c.code || c.id }));
          }
        } catch {}

        // Fallback to live FACR API if cache is empty/unavailable
        if (comps.length === 0 && clubId) {
          try {
            const club = await facrApi.getClub(String(clubId), clubType);
            comps = (club?.competitions || []).map((c: any) => ({ code: c.code, name: c.name || c.code }));
          } catch {}
        }

        // Apply aliases to names for display
        const withAliases = comps.map((c) => ({ code: c.code, name: (c.code && amap[c.code]) ? amap[c.code] : c.name }));
        setAliasesMap(amap);
        setCompetitions(withAliases);
      } catch {}
    })();
  }, []);

  const aiMut = useMutation({
    mutationFn: async () => {
      setAiProgress(0);
      setAiStartTime(Date.now());
      
      // Start progress simulation
      const progressInterval = setInterval(() => {
        setAiProgress(prev => {
          if (prev === undefined || prev >= 95) return prev;
          return Math.min(prev + Math.random() * 15 + 5, 95);
        });
      }, 2000);

      try {
        const parsed = parseInt(String(aiMinWordsInput || '').trim(), 10);
        const effective = Number.isFinite(parsed) && !isNaN(parsed) && parsed > 0 ? parsed : aiMinWords;
        const base = String(aiPrompt || '').trim();
        
        // Add club information to the prompt
        const clubName = settingsQ.data?.club_name || 'Fotbalový klub';
        const clubInfo = `\n\nInformace o klubu:\nNázev klubu: ${clubName}.`;
        
        const htmlGuidelines = [
          'Piš česky a strukturovaně pro blog fotbalového klubu.',
          'Používej bohaté HTML prvky: rozděl článek do 2–4 sekcí s <h2>/<h3>, krátké odstavce <p>, alespoň jeden seznam <ul><li>, zvýraznění <strong>/<em>.',
          'Pokud se to hodí, vlož krátký citát pomocí <blockquote>…</blockquote> (max. 1×).',
          'Nevkládej <html>, <head> ani <body>. Vrať jen validní HTML části obsahu.',
          'Zachovej fakta zadaná uživatelem, vyhýbej se hyperbolem a marketingovým frázím.',
        ].join(' ');
        const finalPrompt = `${base}${clubInfo}\n\n${htmlGuidelines}`.trim();
        const result = await generateBlogAI({
          prompt: finalPrompt,
          audience: aiAudience,
          min_words: effective,
          model: aiModelId,
        });
        
        clearInterval(progressInterval);
        setAiProgress(100);
        
        // Brief moment to show 100% completion
        await new Promise(resolve => setTimeout(resolve, 500));
        
        return result;
      } catch (error) {
        clearInterval(progressInterval);
        throw error;
      }
    },
    onSuccess: (res) => {
      console.log('AI blog response:', res);
      
      // Insert AI output into the editing state
      const aiTitle = cleanAIText(String(res?.title || '').trim());
      const aiSlug = String(res?.slug || '').trim();
      const aiHtml = processAIContent(String(res?.html || '').trim(), { cleanText: true, preserveHtmlTags: true });
      
      if (!aiTitle || !aiHtml) {
        console.error('AI response missing title or html:', res);
        toast({ title: 'AI odpověď neúplná', description: 'Chybí název nebo obsah článku', status: 'error' });
        return;
      }
      
      
      setEditing((prev) => ({
        ...(prev as any),
        title: aiTitle,
        slug: aiSlug,
        content: aiHtml,
      }));
      
      // Clear AI prompt and switch to Základní tab to show results
      setAiPrompt('');
      setActiveTabIndex(1); // Switch to "Základní" tab
      
      toast({ title: 'Článek hotov', description: 'AI rozvinula váš text a vyplnila název, slug a obsah. Zkontrolujte výsledek v záložce Základní.', status: 'success', duration: 5000 });
    },
    onError: (e: any) => {
      console.error('AI generation error:', e);
      toast({ title: 'Generování selhalo', description: e?.response?.data?.error || e?.message || 'Zkuste to prosím znovu.', status: 'error' });
    },
    onSettled: () => {
      setTimeout(() => {
        setAiProgress(undefined);
        setAiStartTime(null);
      }, 1000);
    },
  });

  const openCreate = () => {
    const key = 'draft-article-new';
    setDraftKey(key);
    // Reset match linking state for a brand new article
    setLinkedMatchId('');
    setMatchIdInput('');
    setTempMatchLink('');
    if (localDraft) {
      setEditing(localDraft);
      setActiveTabIndex(1);
    } else {
      setEditing({ title: '', content: '', featured: false, published: false } as any);
      setActiveTabIndex(0);
    }
    setAiPrompt('');
    onOpen();
  };

  const openEdit = (a: Article) => {
    // Set unique draft key for this article
    const key = `draft-article-${a.id}`;
    setDraftKey(key);
    
    setEditing({ 
      ...a, 
      category_name: a.category?.name || a.category_name || '' 
    });
    
    // If match_link is already in the article data, set it immediately
    if ((a as any)?.match_link?.external_match_id) {
      const matchId = String((a as any).match_link.external_match_id);
      setLinkedMatchId(matchId);
      setMatchIdInput(matchId);
    } else {
      // Clear match link state if not present
      setLinkedMatchId('');
      setMatchIdInput('');
    }
    
    setActiveTabIndex(1); // Start on Základní tab for editing
    setAiPrompt(''); // Clear AI prompt
    onOpen();
  };

  const closeModal = () => {
    // Clear temporary match link if user backs out without saving
    setTempMatchLink('');
    setMatchIdInput('');
    setLinkedMatchId('');
    setEditing(null);
    onClose();
    refreshLocalDraft();
  };

  // Draft recovery handlers
  const handleRecoverDraft = () => {
    const draft = loadDraft<EditingArticle>(draftKey);
    if (draft) {
      const isNewDraft = draftKey === 'draft-article-new';
      const restored: any = { ...draft };
      if (isNewDraft && restored.id) {
        delete restored.id;
      }
      setEditing(restored);
      setActiveTabIndex(1);
      onOpen();
    }
    setShowDraftRecovery(false);
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setEditing({ title: '', content: '', featured: false, published: false } as any);
    setActiveTabIndex(0);
    setShowDraftRecovery(false);
    onOpen();
  };

  const handleDeleteOnly = () => {
    clearDraft();
    setShowDraftRecovery(false);
    // Don't open the modal - just delete and close
  };

  const createMut = useMutation({
    mutationFn: (payload: CreateArticlePayload) =>
      // Forward the payload as-is so new fields (youtube, gallery) are persisted
      createArticle(payload),
    onSuccess: async (created: any) => {
      console.log('Article created successfully in mutation callback:', created);
      // Note: Match linking is now handled in onSubmit() to avoid race conditions
      // Clear temporary storage
      setTempMatchLink('');
      setMatchIdInput('');
      try { if (created?.published) { await triggerPrefetch(); } } catch {}
      
      // Invalidate queries to refresh the list
      qc.invalidateQueries({ queryKey: ['admin-articles'] });
      qc.invalidateQueries({ queryKey: ['articles'] });
      qc.invalidateQueries({ queryKey: ['recentArticles'] });
      qc.invalidateQueries({ queryKey: ['article-match-link'] }); // Invalidate match links
      
      // Don't close modal here - let onSubmit handle it after match linking
    },
    onError: (e: any) => {
      console.error('Error creating article:', e);
      toast({ 
        title: 'Chyba při vytváření článku', 
        description: e?.response?.data?.message || 'Došlo k chybě při ukládání',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, payload }: { id: number | string; payload: UpdateArticlePayload }) =>
      // Forward the payload as-is so new fields (youtube, gallery) are persisted
      updateArticle(id, payload),
    onSuccess: async (saved: any, variables) => {
      const articleId = variables.id;
      console.log('Article updated successfully in mutation callback:', articleId);
      try { if (saved?.published) { await triggerPrefetch(); } } catch {}
      
      // Invalidate queries to refresh the list
      qc.invalidateQueries({ queryKey: ['admin-articles'] });
      qc.invalidateQueries({ queryKey: ['articles'] });
      qc.invalidateQueries({ queryKey: ['recentArticles'] });
      qc.invalidateQueries({ queryKey: ['article-match-link', articleId] }); // Invalidate specific match link
      qc.invalidateQueries({ queryKey: ['article', `id:${articleId}`] }); // Invalidate article detail
      
      // Success toast and modal closing handled in onSubmit()
    },
    onError: (e: any) => {
      console.error('Error updating article:', e);
      toast({ 
        title: 'Chyba při aktualizaci článku', 
        description: e?.response?.data?.message || 'Došlo k chybě při ukládání',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number | string) => deleteArticle(id),
    onSuccess: () => {
      toast({ title: 'Článek smazán', status: 'success' });
      qc.invalidateQueries({ queryKey: ['admin-articles'] });
    },
    onError: (e: any) => toast({ title: 'Smazání selhalo', description: e?.response?.data?.message || e?.response?.data?.chyba || 'Chyba', status: 'error' }),
  });

  const handleDeleteArticle = useCallback(
    (article: Partial<Article> & Record<string, any>) => {
      const candidate = article?.id ?? article?.ID ?? article?.article_id ?? article?.articleId;
      if (candidate === undefined || candidate === null || candidate === '') {
        toast({
          title: 'Chybějící ID článku',
          description: 'Nepodařilo se zjistit identifikátor článku pro smazání.',
          status: 'error',
        });
        return;
      }

      const idValue = typeof candidate === 'number' || typeof candidate === 'string'
        ? candidate
        : Number(candidate);

      if (idValue === undefined || idValue === null || (typeof idValue === 'number' && Number.isNaN(idValue))) {
        toast({
          title: 'Neplatné ID článku',
          description: 'Zadaný identifikátor článku není platný.',
          status: 'error',
        });
        return;
      }
    },
    [qc, toast]
  );


  const handleTitleChange = (title: string) => {
    if (!editing) return;
    setEditing(prev => ({
      ...(prev as any),
      title,
      // Auto-generate slug from title if not manually modified
      ...(!(prev as any)?.slugModified ? { 
        slug: makeSlug(title)
      } : {})
    }));
  };

  const handleSlugChange = (slug: string) => {
    if (!editing) return;
    setEditing(prev => ({
      ...(prev as any),
      slug,
      slugModified: true // Mark as manually modified
    }));
  };

  const ensurePlaceholderImage = async (): Promise<string | undefined> => {
    try {
      const logoUrl = String(settingsQ.data?.club_logo_url || '/dist/img/logo-club-empty.svg');
      const canvas = document.createElement('canvas');
      const W = 1200, H = 630;
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext('2d');
      if (!ctx) return undefined;
      // Clean background (white, no black border)
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, W, H);
      // Draw club logo centered
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      const loadImg = () => new Promise<HTMLImageElement>((resolve, reject) => {
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = logoUrl;
      });
      try { await loadImg(); } catch { /* fallback */ }
      const boxW = Math.round(W * 0.5);
      const boxH = Math.round(H * 0.5);
      const x = Math.round((W - boxW) / 2);
      const y = Math.round((H - boxH) / 2);
      try { ctx.drawImage(img, x, y, boxW, boxH); } catch {}
      // Export and upload
      const blob: Blob | null = await new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/jpeg', 0.92));
      if (!blob) return undefined;
      const file = new File([blob], 'article-cover.jpg', { type: 'image/jpeg' });
      const res = await uploadFile(file);
      return (res as any)?.url || undefined;
    } catch { return undefined; }
  };

  const onSubmit = async (options: { keepOpen?: boolean } = {}) => {
    if (!editing) return;

    // Check if content contains raw AI JSON (invalid state)
    const contentText = String(editing.content || '').trim();
    if (contentText.includes('"title":') && contentText.includes('"slug":') && contentText.includes('"html":')) {
      toast({
        title: 'Neplatný obsah',
        description:
          'Obsah obsahuje nezpracovanou AI odpověď. Zkuste AI generování znovu nebo použijte záložku "Základní" pro ruční vytvoření.',
        status: 'error',
        duration: 8000,
      });
      return;
    }

    setManualSaveInProgress(true);

    try {
      const imageUrl = ((editing as any).image_url as string | undefined) || '';

      // Clean HTML content
      const cleanHTML = DOMPurify.sanitize(editing.content || '', {
        USE_PROFILES: { html: true },
        ADD_TAGS: ['iframe'],
        ADD_ATTR: ['target', 'rel', 'allow', 'allowfullscreen'],
      });

      // Prepare the payload according to the backend's expected format
      const payload: CreateArticlePayload = {
        title: editing.title || '',
        content: cleanHTML,
        image_url: imageUrl || '',
        ...(typeof (editing as any).category_id === 'number'
          ? { category_id: (editing as any).category_id as number }
          : {}),
        category_name: (editing as any).category_name || undefined,
        published: true, // Always publish articles
        slug: (editing as any).slug || makeSlug(editing.title || ''),
        featured: !!(editing as any)?.featured,
        // Persist gallery fields when present
        ...((editing as any).gallery_album_id ? { gallery_album_id: (editing as any).gallery_album_id } : {}),
        ...((editing as any).gallery_album_url ? { gallery_album_url: (editing as any).gallery_album_url } : {}),
        ...(Array.isArray((editing as any).gallery_photo_ids) && (editing as any).gallery_photo_ids.length > 0
          ? { gallery_photo_ids: (editing as any).gallery_photo_ids }
          : {}),
        // Persist YouTube fields when present
        ...((editing as any).youtube_video_id ? { youtube_video_id: (editing as any).youtube_video_id } : {}),
        ...((editing as any).youtube_video_title ? { youtube_video_title: (editing as any).youtube_video_title } : {}),
        ...((editing as any).youtube_video_url ? { youtube_video_url: (editing as any).youtube_video_url } : {}),
        ...((editing as any).youtube_video_thumbnail
          ? { youtube_video_thumbnail: (editing as any).youtube_video_thumbnail }
          : {}),
        // Persist attachments when present
        ...(Array.isArray((editing as any)?.attachments) && (editing as any).attachments.length > 0
          ? {
              attachments: (editing as any).attachments.map((a: any) => ({
                name: a.name,
                url: a.url,
                mime_type: a.mime_type,
                size: a.size,
              })),
            }
          : {}),
      };

      // Log the payload for debugging
      console.log('Saving article with payload:', JSON.stringify(payload, null, 2));

      // Debug: Log match link state before submission
      console.log('Match link state before submit:', {
        tempMatchLink,
        matchIdInput,
        linkedMatchId,
        isNewArticle: !(editing as any)?.id,
      });

      if ((editing as any)?.id) {
        // Update existing article
        await updateMut.mutateAsync({ id: (editing as any).id, payload });

        // Handle match linking for existing articles (update or delete)
        const matchRaw = matchIdInput || linkedMatchId;
        const matchId = String(matchRaw || '').trim();
        let matchLinked = false;
        if (matchId) {
          try {
            await putArticleMatchLink((editing as any).id, {
              external_match_id: matchId,
              title: editing.title || '',
            });
            console.log('Match link updated for existing article');
            matchLinked = true;
          } catch (err: any) {
            console.error('Failed to update match link:', err);
          }
        }

        // Show success message
        toast({
          title: matchLinked
            ? 'Článek aktualizován a propojen se zápasem'
            : 'Článek byl úspěšně aktualizován',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new article
        const created = await createMut.mutateAsync(payload);

        // Handle match linking for new articles
        const matchRaw = tempMatchLink || matchIdInput;
        const matchId = String(matchRaw || '').trim();
        if (matchId && created?.id) {
          console.log('Linking new article', created.id, 'with match', matchId);
          try {
            await putArticleMatchLink(created.id, {
              external_match_id: matchId,
              title: editing.title || '',
            });
            console.log('Match link created for new article');
            setLinkedMatchId(matchId);
            toast({
              title: 'Článek vytvořen a propojen se zápasem',
              description: `Match ID: ${matchId}`,
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          } catch (err: any) {
            console.error('Failed to link match:', err);
            toast({
              title: 'Článek vytvořen, ale propojení se zápasem selhalo',
              description:
                err?.response?.data?.error || err?.message || 'Zkuste propojit zápas ručně',
              status: 'warning',
              duration: 5000,
              isClosable: true,
            });
          }
        } else if (created?.id) {
          // No match to link, just show success
          toast({
            title: 'Článek byl úspěšně vytvořen',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }
      }

      // Close modal after successful save (unless keepOpen is true)
      if (!options.keepOpen) {
        closeModal();
      }
    } catch (error: any) {
      console.error('Error saving article:', error);
      // Error is handled by the mutation's onError handler
    } finally {
      setManualSaveInProgress(false);
    }
  };

  const articles = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const uploadAttachmentFile = async (file: File): Promise<UploadPanelFile> => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'text/plain',
      'application/zip',
      'application/x-zip-compressed',
      'application/x-rar-compressed',
      'application/vnd.rar',
    ];
    const isAllowed =
      allowedTypes.includes(file.type) ||
      !!file.name.match(/\.(pdf|docx?|xlsx?|pptx?|jpe?g|png|gif|webp|txt|zip|rar)$/i);
    if (!isAllowed) {
      throw new Error(`Soubor "${file.name}" nelze nahrát (nepodporovaný formát).`);
    }
    const res = await uploadFile(file);
    return {
      url: res.url,
      name: file.name,
      type: file.type || (res as any).type || 'application/octet-stream',
      size: file.size || (res as any).size || 0,
    };
  };

  const handleFeaturedToggle = async (next: boolean) => {
    if (!editing) return;
    const currentIsFeatured = !!(editing as any)?.featured;
    // If turning off or no change, simply update local state
    if (!next || (next === currentIsFeatured)) {
      setEditing((prev) => ({ ...(prev as any), featured: next }));
      return;
    }
    // Turning on featured
    try {
      setFeatSwitchLoading(true);
      // Count featured from current page as a quick check
      let list = (articles || []).filter((a) => a.featured);
      if (list.length >= 3) {
        // Fetch a larger set of featured across pages to be safe
        try {
          const all = await getArticles({ page: 1, page_size: 50, featured: true, published: false });
          list = all.data || list;
        } catch {}
      }
      if (list.length >= 3) {
        // Find the oldest featured article by published_at/created_at/updated_at
        const toTs = (a: any) => {
          const s = a?.published_at || a?.created_at || a?.updated_at || '';
          const t = Date.parse(s);
          return isNaN(t) ? 0 : t;
        };
        const sorted = [...list].sort((a, b) => {
          const dt = toTs(a) - toTs(b);
          if (dt !== 0) return dt;
          // Fallback stable ordering by id
          return (a.id || 0) - (b.id || 0);
        });
        const oldest = sorted[0];
        if (oldest && oldest.id !== (editing as any)?.id) {
          try {
            await updateArticle(oldest.id, { featured: false });
            toast({ title: 'Uvolněno místo pro primární', description: `Zrušena primárnost: ${oldest.title}`, status: 'info' });
            // Refresh caches that may rely on featured
            qc.invalidateQueries({ queryKey: ['admin-articles'] });
            qc.invalidateQueries({ queryKey: ['articles'] });
            qc.invalidateQueries({ queryKey: ['featured-articles'] });
          } catch (e: any) {
            toast({ title: 'Nelze uvolnit místo', description: e?.response?.data?.message || 'Zkuste znovu.', status: 'error' });
            setFeatSwitchLoading(false);
            return;
          }
        }
      }
      setEditing((prev) => ({ ...(prev as any), featured: true }));
    } finally {
      setFeatSwitchLoading(false);
    }
  };

  const matchBgSelected = useColorModeValue('blue.50', 'blue.900');
  const matchBgDefault = useColorModeValue('white', 'gray.700');
  const matchHoverBg = useColorModeValue('blue.50', 'gray.600');
  const albumLinkHasPhotosBg = useColorModeValue('green.50', 'green.900');
  const albumCardBg = useColorModeValue('white', 'gray.700');

  const handleListFeaturedToggle = async (article: Article, next: boolean) => {
    try {
      if (!next) {
        await updateArticle(article.id, { featured: false });
      } else {
        // Enforce max 3 primární články
        let list: Article[] = (articles || []).filter((a) => a.featured);
        const alreadyFeatured = list.some((a) => a.id === article.id);
        if (list.length >= 3 && !alreadyFeatured) {
          try {
            const all = await getArticles({ page: 1, page_size: 50, featured: true, published: false });
            list = all.data || list;
          } catch {}
        }
        if (list.length >= 3 && !alreadyFeatured) {
          const toTs = (a: any) => {
            const s = a?.published_at || a?.created_at || a?.updated_at || '';
            const t = Date.parse(s);
            return Number.isNaN(t) ? 0 : t;
          };
          const sorted = [...list].sort((a, b) => {
            const dt = toTs(a) - toTs(b);
            if (dt !== 0) return dt;
            return (a.id || 0) - (b.id || 0);
          });
          const oldest = sorted[0];
          if (oldest && oldest.id !== article.id) {
            try {
              await updateArticle(oldest.id, { featured: false });
              toast({ title: 'Uvolněno místo pro primární', description: `Zrušena primárnost: ${oldest.title}`, status: 'info' });
            } catch (e: any) {
              toast({ title: 'Nelze uvolnit místo', description: e?.response?.data?.message || 'Zkuste znovu.', status: 'error' });
              return;
            }
          }
        }
        await updateArticle(article.id, { featured: true });
      }

      qc.invalidateQueries({ queryKey: ['admin-articles'] });
      qc.invalidateQueries({ queryKey: ['articles'] });
      qc.invalidateQueries({ queryKey: ['featured-articles'] });
      toast({
        title: next ? 'Článek nastaven jako primární' : 'Článek odstraněn z primárních',
        status: 'success',
        duration: 2000,
      });
    } catch (error: any) {
      toast({
        title: 'Chyba při aktualizaci',
        description: error?.response?.data?.error || 'Nepodařilo se změnit stav',
        status: 'error',
      });
    }
  };

  // Quick translation handler for article list
  const handleQuickTranslate = async (article: Article) => {
    if (!article.title || !article.content) {
      toast({
        title: 'Nelze přeložit',
        description: 'Článek musí mít název a obsah.',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      const sourceLang = detectLanguage(article.title + ' ' + article.content);
      const targetLang = sourceLang === 'cs' ? 'en' : 'cs';

      if (sourceLang === targetLang) {
        toast({
          title: 'Překlad není potřeba',
          description: 'Článek je již v cílovém jazyce.',
          status: 'info',
          duration: 3000,
        });
        return;
      }

      const result = await translateBlogContent(article.title, article.content, sourceLang, targetLang);
      
      // Update the article with translated content
      await updateArticle(article.id, {
        title: result.translatedTitle,
        content: result.translatedContent,
        slug: makeSlug(result.translatedTitle),
      });

      // Refresh the articles list
      qc.invalidateQueries({ queryKey: ['admin-articles'] });
      qc.invalidateQueries({ queryKey: ['articles'] });

      toast({
        title: 'Článek přeložen',
        description: `Článek byl přeložen z ${sourceLang === 'cs' ? 'češtiny' : 'angličtiny'} do ${targetLang === 'cs' ? 'češtiny' : 'angličtiny'}.`,
        status: 'success',
        duration: 3000,
      });
    } catch (error: any) {
      toast({
        title: 'Chyba při překladu',
        description: error?.message || 'Nepodařilo se přeložit článek.',
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <AdminLayout requireAdmin={false}>
      <Box>
        <HStack justify="space-between" mb={4}>
          <Heading size="lg">Články</Heading>
          <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={openCreate}>
            Nový článek
          </Button>
        </HStack>
        <Text color="gray.500" mb={6}>
          Správa článků a novinek na webu. Můžete vytvářet, upravovat a publikovat články.
        </Text>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
          <Input
            placeholder="Hledat články..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            mr={2}
          />
        </SimpleGrid>

        <Box 
          bg={useColorModeValue('white', 'gray.800')}
          borderWidth="1px"
          borderRadius="lg"
          overflowX="auto"
          boxShadow="sm"
          mb={6}
        >
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Obrázek</Th>
                <Th>Titulek</Th>
                <Th>Kategorie</Th>
                <Th>⭐ Primární</Th>
                <Th>Zápas</Th>
                <Th isNumeric>Akce</Th>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading && (
                <Tr><Td colSpan={6}><Spinner size="sm" /></Td></Tr>
              )}
              {!isLoading && localDraft && (
                <Tr key="local-draft" opacity={0.6}>
                  <Td>
                    <ThumbnailPreview 
                      src={assetUrl((localDraft as any).image_url) || '/dist/img/logo-club-empty.svg'} 
                      alt={(localDraft as any).title || 'Koncept'} 
                      size="48px"
                      previewSize="350px"
                    />
                  </Td>
                  <Td>
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="medium">{(localDraft as any).title || 'Bez názvu (koncept)'}</Text>
                      <Text fontSize="xs" color="gray.500">Koncept (lokálně uložený)</Text>
                    </VStack>
                  </Td>
                  <Td>
                    <Badge colorScheme="gray" fontSize="xs">
                      {(localDraft as any).category_name || 'Bez kategorie'}
                    </Badge>
                  </Td>
                  <Td>
                    <Switch size="sm" isChecked={!!(localDraft as any).featured} isDisabled />
                  </Td>
                  <Td>
                    <Badge colorScheme="gray">Koncept</Badge>
                  </Td>
                  <Td isNumeric>
                    <HStack spacing={1} justify="flex-end">
                      <IconButton aria-label="Upravit koncept" size="sm" icon={<FiEdit2 />} onClick={openCreate} />
                      <IconButton 
                        aria-label="Smazat koncept" 
                        size="sm" 
                        colorScheme="red" 
                        icon={<FiTrash2 />} 
                        onClick={() => { try { localStorage.removeItem('draft-article-new'); } catch {} setLocalDraft(null); toast({ title: 'Koncept odstraněn', status: 'success', duration: 2000 }); }} 
                      />
                    </HStack>
                  </Td>
                </Tr>
              )}
              {!isLoading && articles.map((a) => (
                <Tr key={a.id} opacity={a.published ? 1 : 0.6}>
                  <Td>
                    <ThumbnailPreview 
                      src={assetUrl(a.image_url) || '/dist/img/logo-club-empty.svg'} 
                      alt={a.title} 
                      size="48px"
                      previewSize="350px"
                    />
                  </Td>
                  <Td>
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="medium">{a.title}</Text>
                      <Text fontSize="xs" color="gray.500">
                        {a.published ? '✓ Publikováno' : '○ Koncept'}
                      </Text>
                    </VStack>
                  </Td>
                  <Td>
                    <Badge colorScheme="blue" fontSize="xs">
                      {a.category?.name || a.category_name || 'Bez kategorie'}
                    </Badge>
                  </Td>
                  <Td>
                    <Switch
                      size="sm"
                      isChecked={!!a.featured}
                      onChange={(e) => {
                        handleListFeaturedToggle(a, e.target.checked);
                      }}
                    />
                  </Td>
                  <Td><MatchLinkBadge articleId={a.id} /></Td>
                  <Td isNumeric>
                    <HStack spacing={1}>
                      <IconButton aria-label="Upravit" size="sm" icon={<FiEdit2 />} onClick={() => openEdit(a)} />
                      <IconButton 
                        aria-label="Přeložit" 
                        size="sm" 
                        colorScheme="blue" 
                        icon={<FiGlobe />} 
                        title="Přeložit článek"
                        onClick={() => handleQuickTranslate(a)} 
                      />
                      <IconButton aria-label="Smazat" size="sm" colorScheme="red" icon={<FiTrash2 />} onClick={() => handleDeleteArticle(a)} />
                      <IconButton
                        aria-label="Zkrátit odkaz"
                        size="sm"
                        icon={<FiLink />}
                        title="Zkrátit odkaz pro sdílení"
                        onClick={async () => {
                          try {
                            const origin = window.location.origin;
                            const slug = (a as any)?.slug || (a as any)?.Slug;
                            const path = slug ? `/news/${slug}` : `/articles/${a.id}`;
                            const target = `${origin}${path}`;
                            const res = await createShortLink({ target_url: target, title: a.title, source_type: 'article', source_id: a.id, code: `a${a.id}` });
                            await navigator.clipboard.writeText(res.short_url);
                            toast({ title: 'Zkrácený odkaz zkopírován', description: res.short_url, status: 'success', duration: 4000 });
                          } catch (e: any) {
                            toast({ title: 'Vytvoření odkazu selhalo', description: e?.message || 'Zkuste to znovu', status: 'error' });
                          }
                        }}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        <HStack mt={4} justify="flex-end">
          <Button onClick={() => setPage((p) => Math.max(1, p - 1))} isDisabled={page <= 1}>Předchozí</Button>
          <Button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} isDisabled={page >= totalPages}>Další</Button>
        </HStack>

      {/* Create/Edit Modal (90% viewport) */}
      <Modal isOpen={isOpen} onClose={closeModal} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent maxW="90vw" maxH="90vh">
          <ModalHeader>
            <HStack justify="space-between" align="center" w="full" pr={8}>
              <Text>{(editing as any)?.id ? 'Upravit článek' : 'Nový článek'}</Text>
              <SaveStatusIndicator status={saveStatus} lastSaved={lastSaved} />
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody maxH="calc(90vh - 120px)" overflowY="auto">
            <Tabs variant="enclosed" colorScheme="blue" isFitted index={activeTabIndex} onChange={(index) => setActiveTabIndex(index)} isLazy lazyBehavior="keepMounted">
              <TabList>
                <Tab>AI</Tab>
                <Tab>Základní</Tab>
                <Tab>Obsah</Tab>
                <Tab>Média</Tab>
                <Tab>Anketa</Tab>
              </TabList>
              <TabPanels>
                {/* AI first */}
                <TabPanel>
                  <Box borderWidth="1px" borderRadius="md" p={4} bg="blue.50">
                    <Heading as="h3" size="sm" mb={2}>✍️ AI: Rozviňte VÁŠ TEXT na článek</Heading>
                    <Text fontSize="sm" color="gray.700" mb={3}>
                      <strong>Napište svůj text</strong> s důležitými informacemi - fakta, jména, výsledky, události. 
                      AI váš text použije jako základ, přidá strukturu a pokud je text krátký, rozšíří ho o kontext a detaily 
                      kolem vašich informací. <strong>Čím více napíšete, tím méně AI přidává.</strong> Dlouhý text = jen formátování.
                    </Text>
                    <VStack align="stretch" spacing={3}>
                      <FormControl>
                        <FormLabel fontWeight="bold">Váš text (česky)</FormLabel>
                        <AIPromptInput
                          value={aiPrompt}
                          onChange={setAiPrompt}
                          onSubmit={(_, modelId) => {
                            setAiModelId(modelId);
                            return aiMut.mutateAsync();
                          }}
                          isSubmitting={aiMut.isLoading}
                          placeholder={"Napište svůj text s důležitými informacemi. Příklad: Dnes naše mužstvo zvládlo důležitý zápas proti TJ Sokol Příbram..."}
                          helperText={"Krátký text AI rozšíří, dlouhý text hlavně naformátuje do HTML."}
                        />
                      </FormControl>
                      <HStack>
                        <FormControl>
                          <FormLabel>Publikum</FormLabel>
                          <Input value={aiAudience} onChange={(e) => setAiAudience(e.target.value)} bg={inputBg} />
                        </FormControl>
                        <FormControl w="180px">
                          <FormLabel>Min. slov</FormLabel>
                          <Input
                            type="text"
                            inputMode="numeric"
                            placeholder="500"
                            value={aiMinWordsInput}
                            onChange={(e) => {
                              const v = e.target.value;
                              const digits = v.replace(/[^0-9]/g, '');
                              setAiMinWordsInput(digits);
                            }}
                            onBlur={() => {
                              const n = parseInt(String(aiMinWordsInput || '').trim(), 10);
                              if (!isNaN(n) && Number.isFinite(n) && n > 0) {
                                setAiMinWords(n);
                                setAiMinWordsInput(String(n));
                              } else {
                                // Reset to last valid numeric value
                                setAiMinWordsInput(String(aiMinWords || 500));
                              }
                            }}
                            bg={inputBg}
                          />
                        </FormControl>
                      </HStack>
                      <HStack>
                        <Button size="md" colorScheme="purple" onClick={() => aiMut.mutateAsync()} isDisabled={aiMut.isLoading || !aiPrompt.trim()}>
                          Rozvinout text na článek
                        </Button>
                        <Box fontSize="sm" color="gray.600">AI zachová váš text a rozšíří ho pokud je krátký, přidá strukturu a vygeneruje název.</Box>
                      </HStack>
                    </VStack>
                  </Box>
                </TabPanel>

                {/* Základní second (includes match linking) */}
                <TabPanel>
                  <VStack align="stretch" spacing={4}>
                    <FormControl isRequired>
                      <FormLabel fontWeight="bold">Název článku</FormLabel>
                      <Input 
                        value={editing?.title || ''} 
                        onChange={(e) => handleTitleChange(e.target.value)} 
                        placeholder="Zadejte název článku"
                        size="lg"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>URL adresa (slug)</FormLabel>
                      <Input 
                        placeholder="moje-clanek-slug" 
                        value={(editing as any)?.slug || ''} 
                        onChange={(e) => handleSlugChange(e.target.value)} 
                      />
                      <FormHelperText>Automaticky generováno z názvu článku</FormHelperText>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontWeight="bold">Kategorie (soutěž)</FormLabel>
                      <Select
                        placeholder="Vyberte kategorii (volitelné)"
                        value={(editing as any)?.category_name || ''}
                        onChange={(e) => setEditing((prev) => ({ ...(prev as any), category_name: e.target.value }))}
                        size="lg"
                      >
                        {competitions.map((c, idx) => (
                          <option key={(c.code || c.name) + '_' + idx} value={(c.code && aliasesMap[c.code]) ? aliasesMap[c.code] : c.name}>
                            {(c.code && aliasesMap[c.code]) ? aliasesMap[c.code] : c.name}
                          </option>
                        ))}
                      </Select>
                      <FormHelperText>Kategorie určuje, ve které sekci se článek zobrazí (volitelné)</FormHelperText>
                    </FormControl>
                    
                    {/* Blog Translation */}
                    {editing?.title && editing?.content && (
                      <BlogTranslator
                        title={editing.title}
                        content={editing.content}
                        onTranslationComplete={(translatedTitle, translatedContent) => {
                          setEditing((prev) => ({
                            ...(prev as any),
                            title: translatedTitle,
                            content: translatedContent,
                            slug: makeSlug(translatedTitle),
                          }));
                          toast({
                            title: 'Článek přeložen',
                            description: 'Článek byl úspěšně přeložen a uložen do formuláře.',
                            status: 'success',
                            duration: 3000,
                          });
                        }}
                      />
                    )}
                    
                    {/* Featured toggle - prominent display */}
                    <Box 
                      borderWidth="2px" 
                      borderRadius="lg" 
                      p={4} 
                      bg={useColorModeValue('orange.50', 'orange.900')} 
                      borderColor={useColorModeValue('orange.300', 'orange.600')}
                    >
                      <FormControl display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <FormLabel mb="1" fontSize="lg" fontWeight="bold">⭐ Primární na úvodní stránce</FormLabel>
                          <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.400')}>Zvýrazní článek jako hlavní příspěvek na domovské stránce</Text>
                          <Text fontSize="xs" color={useColorModeValue('gray.600', 'gray.400')} mt={1}>
                            Maximálně 3 články mohou být primární. Pokud vyberete další, nejstarší primární se automaticky zruší.
                          </Text>
                        </Box>
                        <Switch
                          size="lg"
                          isChecked={!!(editing as any)?.featured}
                          isDisabled={featSwitchLoading}
                          onChange={(e) => handleFeaturedToggle(e.target.checked)}
                        />
                      </FormControl>
                    </Box>

                    {/* Propojení se zápasem (vylepšený visual picker) */}
                    <Box borderWidth="1px" borderRadius="lg" p={4} bg={useColorModeValue('blue.50', 'blue.900')} borderColor="blue.200">
                      <HStack justify="space-between" mb={3}>
                        <Heading as="h3" size="sm">⚽ Propojit se zápasem (volitelné)</Heading>
                        {linkedMatchId && <Badge colorScheme="green">Propojeno</Badge>}
                      </HStack>
                      <VStack align="stretch" spacing={3}>
                        {!(editing as any)?.category_name && (
                          <Text fontSize="sm" color="orange.600" fontWeight="bold">
                            ⚠️ Nejprve vyberte kategorii výše pro zobrazení relevantních zápasů
                          </Text>
                        )}
                        <FormControl isDisabled={!((editing as any)?.category_name)}>
                          <FormLabel fontSize="sm">Rychlé vyhledávání</FormLabel>
                          <HStack spacing={2}>
                            <Input 
                              placeholder="Hledat zápas (tým, soupeř...)" 
                              value={matchSearch} 
                              onChange={(e)=> setMatchSearch(e.target.value)}
                              bg={inputBg}
                              size="sm"
                            />
                            <Input 
                              type="date" 
                              value={matchDateFilter}
                              onChange={(e)=> setMatchDateFilter(e.target.value)}
                              bg={inputBg}
                              size="sm"
                              w="160px"
                            />
                          </HStack>
                        </FormControl>
                        
                        {/* Calendar-style match picker */}
                        <Box>
                          <HStack justify="space-between" mb={2}>
                            <Text fontSize="sm" fontWeight="medium" mb={0}>Vyberte zápas ({filteredMatchOptions.length})</Text>
                            {linkedMatchId && (editing as any)?.id && (
                              <Button size="xs" variant="ghost" colorScheme="red" onClick={async()=>{
                                try {
                                  await deleteArticleMatchLink((editing as any).id);
                                  setLinkedMatchId(''); 
                                  setMatchIdInput('');
                                  setTempMatchLink('');
                                  toast({ title: 'Propojení zrušeno', status: 'success' });
                                } catch(e:any) {
                                  toast({ title: 'Zrušení selhalo', description: e?.response?.data?.error || e?.message, status: 'error' });
                                }
                              }}>Zrušit propojení</Button>
                            )}
                          </HStack>
                          
                          <Box 
                            maxH="400px" 
                            overflowY="auto" 
                            bg={useColorModeValue('white', 'gray.800')}
                            borderRadius="md"
                            borderWidth="1px"
                            p={2}
                          >
                            {filteredMatchOptions.length === 0 && (
                              <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                                Žádné zápasy pro vybranou kategorii
                              </Text>
                            )}
                            <VStack spacing={2} align="stretch">
                              {filteredMatchOptions.slice(0, 100).map((match) => {
                                const matchId = String(match.id || '');
                                const isPersisted = linkedMatchId === matchId;
                                const isTempSelected = matchIdInput === matchId && !isPersisted;
                                const isSelected = isPersisted || isTempSelected;
                                const matchDate = new Date(match.date || '');
                                const isValidDate = !isNaN(matchDate.getTime());
                                const dateStr = isValidDate ? matchDate.toLocaleDateString('cs-CZ', { 
                                  day: 'numeric', 
                                  month: 'short',
                                  year: 'numeric'
                                }) : (match.date || 'N/A');
                                const timeStr = isValidDate ? matchDate.toLocaleTimeString('cs-CZ', { 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                }) : '';
                                
                                // Use match data directly
                                const home = match.home || '';
                                const away = match.away || '';
                                const score = match.score || 'vs';
                                const hasScore = score !== 'vs';
                                const teams = `${home} ${score} ${away}`.trim();
                                
                                return (
                                  <Box
                                    key={match.id}
                                    p={3}
                                    borderWidth="2px"
                                    borderRadius="md"
                                    borderColor={isPersisted ? 'green.500' : (isTempSelected ? 'blue.500' : 'gray.200')}
                                    bg={isSelected ? matchBgSelected : matchBgDefault}
                                    cursor="pointer"
                                    _hover={{ borderColor: isPersisted ? 'green.400' : 'blue.300', bg: matchHoverBg }}
                                    transition="all 0.2s"
                                    onClick={async () => {
                                      const val = matchId;
                                      setMatchIdInput(val);
                                      setTempMatchLink(val);
                                      // Save immediately when an article already exists
                                      if (val && (editing as any)?.id) {
                                        try {
                                          const resp = await putArticleMatchLink((editing as any).id, { external_match_id: String(val).trim(), title: (editing as any)?.title || '' });
                                          setLinkedMatchId(resp.external_match_id || String(val).trim());
                                          toast({ title: 'Propojeno se zápasem', status: 'success', duration: 2000 });
                                        } catch (err: any) {
                                          toast({ title: 'Propojení selhalo', description: err?.response?.data?.error || err?.message, status: 'error' });
                                        }
                                      } else if (val && !(editing as any)?.id) {
                                        toast({ title: 'Propojení bude uloženo po vytvoření článku', status: 'info', duration: 2000 });
                                      }
                                    }}
                                  >
                                    <HStack justify="space-between" spacing={3}>
                                      <VStack align="start" spacing={1} flex={1}>
                                        <HStack spacing={2}>
                                          <Badge colorScheme={hasScore ? 'green' : 'gray'} fontSize="xs">
                                            {dateStr}
                                          </Badge>
                                          <Badge colorScheme="blue" fontSize="xs">
                                            {timeStr}
                                          </Badge>
                                        </HStack>
                                        <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
                                          {teams}
                                        </Text>
                                        {match.competition && (
                                          <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                            {match.competition}
                                          </Text>
                                        )}
                                      </VStack>
                                      {isPersisted && (
                                        <Badge colorScheme="green" fontSize="xs" p={2}>
                                          Již má článek
                                        </Badge>
                                      )}
                                      {!isPersisted && isTempSelected && (
                                        <Badge colorScheme="blue" fontSize="xs" p={2}>
                                          Vybráno
                                        </Badge>
                                      )}
                                    </HStack>
                                  </Box>
                                );
                              })}
                            </VStack>
                          </Box>
                          {filteredMatchOptions.length > 100 && (
                            <Text fontSize="xs" color="gray.500" mt={2}>
                              Zobrazeno prvních 100 zápasů. Použijte vyhledávání pro zúžení výsledků.
                            </Text>
                          )}
                        </Box>
                      </VStack>
                    </Box>
                  </VStack>
                </TabPanel>

                {/* Obsah third */}
                <TabPanel>
                  <FormControl>
                    <FormLabel>Obsah</FormLabel>
                    <HStack justify="space-between" mb={2}>
                      <Button 
                        size="sm" 
                        colorScheme="purple" 
                        leftIcon={<FiPlus />}
                        onClick={onAlbumPickerOpen}
                      >
                        Vložit fotografie z alba
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<FiSearch />}
                        onClick={onExistingAlbumsOpen}
                      >
                        Vybrat z alba
                      </Button>
                    </HStack>
                    {activeTabIndex === 2 && (
                      <RichTextEditor
                        value={editing?.content || ''}
                        onChange={(val: string) => {
                          setLastContentChangeAt(Date.now());
                          setEditing((prev) => ({ ...(prev as any), content: val }));
                        }}
                        placeholder="Začněte psát obsah článku..."
                        height="60vh"
                        onImageUpload={uploadFile}
                        toolbar="full"
                      />
                    )}
                  </FormControl>
                </TabPanel>

                {/* Média fourth */}
                <TabPanel>
                  <VStack align="stretch" spacing={4}>
                    {/* Zonerama picker - Album link only */}
                    <Box borderWidth="1px" borderRadius="md" p={3} bg="gray.50">
                      <Heading as="h3" size="sm" mb={2}>Zonerama výběr</Heading>
                      <VStack align="stretch" spacing={2}>
                        <FormControl>
                          <FormLabel>Odkaz na album Zonerama</FormLabel>
                          <InputGroup>
                            <InputLeftElement pointerEvents="none">
                              <Icon as={FiSearch} color="gray.400" />
                            </InputLeftElement>
                            <Input 
                              placeholder="https://eu.zonerama.com/…" 
                              value={zAlbumLink} 
                              onChange={(e) => setZAlbumLink(e.target.value)}
                              bg={zAlbumPhotos.length > 0 ? albumLinkHasPhotosBg : undefined}
                            />
                          </InputGroup>
                          <FormHelperText fontSize="xs">
                            {zAlbumPhotos.length > 0 
                              ? `✓ Album načteno (${zAlbumPhotos.length} fotografií). Můžete vložit jiné album nebo vybrat fotky níže.`
                              : 'Vložte odkaz na album, nebo album se automaticky načte při výběru fotografií v sekci Obsah.'}
                          </FormHelperText>
                          {(editing as any)?.gallery_album_url && zAlbumLink && (editing as any).gallery_album_url === zAlbumLink && (
                            <Text fontSize="xs" color="blue.600" fontWeight="bold" mt={1}>
                              🔗 Album propojeno s článkem (zobrazeno v sekci Obsah)
                            </Text>
                          )}
                        </FormControl>
                        <HStack>
                          <Button size="sm" onClick={fetchAlbumByLink} isLoading={zLoading}>Načíst album</Button>
                          <Button size="sm" colorScheme="purple" onClick={onGalleryPickerOpen}>Vybrat z galerie</Button>
                          {zAlbumLink ? (
                            <Button size="sm" as="a" href={zAlbumLink} target="_blank" rel="noopener noreferrer" rightIcon={<FiExternalLink />}>Otevřít album</Button>
                          ) : null}
                        </HStack>
                        {zLoading && zAlbumPhotos.length === 0 && (
                          <HStack spacing={2} color="gray.600" mt={2}>
                            <Spinner size="sm" />
                            <Text>Album se načítá…</Text>
                          </HStack>
                        )}
                        {!zLoading && zAlbumPhotos.length === 0 && zAlbumLink && (
                          <Text fontSize="sm" color="gray.500" mt={2}>Zadejte odkaz na Zonerama album a klikněte na "Načíst album"</Text>
                        )}
                        {zAlbumPhotos.length > 0 && (
                          <>
                            <SimpleGrid columns={{ base: 3, md: 6 }} spacing={2} mt={2}>
                              {zAlbumPhotos.slice(0, zVisibleCount).map((p) => (
                                <Box key={p.id} borderWidth="1px" borderRadius="md" overflow="hidden" _hover={{ boxShadow: 'md' }} cursor="pointer"
                                  onClick={() => pickZoneramaImage({ id: p.id, album_id: '', album_url: zAlbumLink, page_url: p.page_url, image_url: p.image_1500 || '', title: p.title })}
                                >
                                  <Image src={p.image_1500 || ''} alt={p.id} w="100%" h="100px" objectFit="cover" loading="lazy" decoding="async" />
                                </Box>
                              ))}
                            </SimpleGrid>
                            {zAlbumPhotos.length > zVisibleCount && (
                              <HStack justify="center" mt={2}>
                                <Button size="sm" onClick={() => setZVisibleCount((c) => c + 24)}>Načíst další</Button>
                              </HStack>
                            )}
                          </>
                        )}
                      </VStack>
                    </Box>

                    {/* Titulní obrázek - Cover Image */}
                    <FormControl>
                      <FormLabel fontWeight="bold">Titulní obrázek</FormLabel>
                      <FormHelperText mb={2}>
                        Vyberte obrázek ze Zonerama výše nebo nahrajte vlastní soubor.
                      </FormHelperText>
                      <VStack align="stretch" spacing={3}>
                        <UploadPanel
                          label="Titulní obrázek"
                          description="Nahrajte nebo přetáhněte titulní obrázek článku."
                          accept="image/*"
                          multiple={false}
                          maxFiles={1}
                          allowUrlImport
                          urlPlaceholder="/uploads/... nebo https://example.com/obrazek.jpg"
                          onChange={(files) => {
                            const last = files[files.length - 1];
                            setEditing((prev) => ({
                              ...(prev as any),
                              image_url: last?.url || (prev as any)?.image_url || '',
                            }));
                          }}
                        />
                        <HStack>
                          <Image
                            src={assetUrl(editing?.image_url) || '/dist/img/logo-club-empty.svg'}
                            alt="cover"
                            boxSize="80px"
                            objectFit="cover"
                            borderRadius="md"
                          />
                          {editing?.image_url && (
                            <Text fontSize="sm" color="gray.500" noOfLines={1}>
                              {editing.image_url}
                            </Text>
                          )}
                        </HStack>
                        <HStack justify="space-between">
                          <Text fontSize="xs" color="gray.500">
                            Pokud necháte obrázek prázdný, může být při uložení článku vygenerován automaticky.
                          </Text>
                          <Button
                            size="xs"
                            variant="outline"
                            colorScheme="purple"
                            onClick={handleGenerateMainImage}
                            isLoading={mainImageLoading}
                          >
                            Vygenerovat titulní obrázek (Grok)
                          </Button>
                        </HStack>
                      </VStack>
                    </FormControl>

                    {/* YouTube video attachment */}
                    <Box borderWidth="1px" borderRadius="md" p={3} bg="gray.50">
                      <HStack justify="space-between" align="center" mb={2}>
                        <Heading as="h3" size="sm" display="flex" alignItems="center" gap={2}>
                          <Icon as={FiVideo} />
                          YouTube video k článku
                        </Heading>
                        <Button size="sm" variant="outline" leftIcon={<FiVideo />} onClick={onYouTubeModalOpen}>
                          Vybrat z kanálu
                        </Button>
                      </HStack>

                      <VStack align="stretch" spacing={3}>
                        <FormControl>
                          <FormLabel fontSize="sm">Přidat ručně (ID nebo odkaz)</FormLabel>
                          <HStack>
                            <Input
                              placeholder="https://www.youtube.com/watch?v=..."
                              value={youtubeManualInput}
                              onChange={(e) => setYoutubeManualInput(e.target.value)}
                              bg={inputBg}
                            />
                            <Button size="sm" colorScheme="blue" onClick={handleManualYoutubeSubmit}>
                              Přidat
                            </Button>
                          </HStack>
                          <FormHelperText fontSize="xs">Podporujeme plné URL, zkrácené odkazy i ID videa.</FormHelperText>
                        </FormControl>

                        {(editing as any)?.youtube_video_id ? (
                          <Box borderWidth="1px" borderRadius="md" p={3} bg={inputBg}>
                            <HStack align="start" spacing={3}>
                              <AspectRatio ratio={16 / 9} w="160px">
                                <Image
                                  src={(editing as any)?.youtube_video_thumbnail || `https://i.ytimg.com/vi/${(editing as any)?.youtube_video_id}/hqdefault.jpg`}
                                  alt={(editing as any)?.youtube_video_title || 'YouTube thumbnail'}
                                  objectFit="cover"
                                  borderRadius="md"
                                />
                              </AspectRatio>
                              <VStack align="stretch" spacing={1} flex={1}>
                                <Text fontWeight="semibold">{(editing as any)?.youtube_video_title || 'YouTube video'}</Text>
                                <Link href={(editing as any)?.youtube_video_url} isExternal color="blue.500">
                                  {(editing as any)?.youtube_video_url}
                                </Link>
                                <HStack spacing={2}>
                                  <Badge colorScheme="red">ID: {(editing as any)?.youtube_video_id}</Badge>
                                </HStack>
                              </VStack>
                              <IconButton
                                aria-label="Odebrat video"
                                icon={<FiX />}
                                size="sm"
                                onClick={handleClearYoutubeVideo}
                              />
                            </HStack>
                          </Box>
                        ) : (
                          <Text fontSize="sm" color="gray.600">Žádné video není připojeno. Vyberte video z kanálu nebo vložte odkaz.</Text>
                        )}
                      </VStack>
                    </Box>

                    

                    {/* File Attachments */}
                    <FormControl>
                      <FormLabel fontWeight="bold">Přílohy</FormLabel>
                      <FormHelperText mb={2}>
                        Přidejte dokumenty, obrázky nebo jiné soubory k článku (PDF, Word, Excel, PowerPoint, obrázky, ZIP)
                      </FormHelperText>
                      <UploadPanel
                        label="Přílohy k článku"
                        description="Nahrajte dokumenty, obrázky nebo archivy jako přílohy k článku."
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.txt,.zip,.rar"
                        multiple
                        maxFiles={30}
                        uploadFn={uploadAttachmentFile}
                        onUploadFinished={(uploaded) => {
                          if (!uploaded || uploaded.length === 0) return;
                          setEditing((prev) => {
                            const prevAtts = Array.isArray((prev as any)?.attachments)
                              ? (prev as any).attachments
                              : [];
                            const mapped = uploaded.map((f) => ({
                              name: f.name,
                              url: f.url,
                              mime_type: f.type,
                              size: f.size,
                            }));
                            return {
                              ...(prev as any),
                              attachments: [...prevAtts, ...mapped],
                            };
                          });
                        }}
                      />
                      <Box mt={2}>
                        {Array.isArray((editing as any)?.attachments) && (editing as any).attachments.length > 0 ? (
                          <Table size="sm" variant="simple">
                            <Thead>
                              <Tr>
                                <Th>Název</Th>
                                <Th>Velikost</Th>
                                <Th>Akce</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {((editing as any).attachments as any[]).map((att: any, idx: number) => (
                                <Tr key={idx}>
                                  <Td colSpan={3} p={2}>
                                    <HStack justify="space-between" w="full">
                                      <Box flex={1}>
                                        <FilePreview 
                                          url={att.url}
                                          name={att.name}
                                          mimeType={att.mime_type}
                                          size={att.size}
                                        />
                                      </Box>
                                      <Button 
                                        size="xs" 
                                        variant="outline" 
                                        colorScheme="red"
                                        flexShrink={0}
                                        ml={2}
                                        onClick={() => setEditing(prev => ({ 
                                          ...(prev as any), 
                                          attachments: ((prev as any).attachments || []).filter((_: any, i: number) => i !== idx) 
                                        }))}
                                      >
                                        Odebrat
                                      </Button>
                                    </HStack>
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        ) : (
                          <Box color="gray.500" fontSize="sm">Žádné přílohy</Box>
                        )}
                      </Box>
                    </FormControl>

                  </VStack>
                </TabPanel>

                {/* Anketa (Poll) Tab */}
                <TabPanel>
                  <VStack align="stretch" spacing={4}>
                    <Box borderWidth="1px" borderRadius="md" p={4} bg={useColorModeValue('purple.50', 'purple.900')}>
                      <Heading as="h3" size="sm" mb={2}>📊 Ankety k článku</Heading>
                      <Text fontSize="sm" color="gray.700" mb={3}>
                        Vytvořte nebo připojte ankety přímo k tomuto článku. Ankety se zobrazí automaticky na konci článku a čtenáři mohou hlasovat.
                      </Text>
                    </Box>

                    {isAdmin && editing?.id ? (
                      <PollLinker articleId={editing.id} onPollsChanged={() => {
                        // Invalidate queries to refresh polls
                        qc.invalidateQueries({ queryKey: ['linked-polls'] });
                      }} />
                    ) : (
                      <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <VStack align="start" spacing={2}>
                          <Text fontWeight="semibold">
                            {saveStatus === 'saving' ? 'Ukládání článku...' : 'Článek se ukládá automaticky'}
                          </Text>
                          <Text fontSize="sm">
                            Začněte psát článek na záložkách výše. Systém automaticky ukládá každou změnu jako koncept. Jakmile bude článek uložen (v záhlaví se zobrazí "Uloženo"), budete moci přidat ankety.
                          </Text>
                          {saveStatus === 'saving' && <Spinner size="sm" color="blue.500" />}
                          {saveStatus === 'idle' && (
                            <Text fontSize="xs" color="gray.600">
                              💡 Vyplňte název článku pro aktivaci automatického ukládání
                            </Text>
                          )}
                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={async () => {
                              // Force save if needed
                              try {
                                await forceSave();
                                // Switch to poll tab after save
                                setActiveTabIndex(4); // Poll tab is index 4 after reordering
                              } catch (error) {
                                // Error is handled by onSubmit
                              }
                            }}
                            isLoading={createMut.isLoading}
                          >
                            Uložit jako koncept a přidat ankety
                          </Button>
                        </VStack>
                      </Alert>
                    )}
                  </VStack>
                </TabPanel>

              </TabPanels>
            </Tabs>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={closeModal}>Zrušit</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Album Photo Picker Modal */}
      <AlbumPhotoPicker
        isOpen={isAlbumPickerOpen}
        onClose={onAlbumPickerClose}
        onPhotosSelected={handleAlbumPhotosSelected}
      />

      
      <Modal isOpen={isExistingAlbumsOpen} onClose={() => { setExistingSelectedAlbum(null); setExistingSelectedPhotos(new Set()); setExistingVisibleCount(60); onExistingAlbumsClose(); }} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="90vh" display="flex" flexDirection="column">
          <ModalHeader borderBottom="1px solid" borderColor={useColorModeValue('gray.200', 'gray.700')}>Vybrat z existujících alb</ModalHeader>
          <ModalCloseButton />
          <ModalBody flex="1" overflowY="auto" py={6} px={6}>
            <VStack align="stretch" spacing={4}>
              {!existingSelectedAlbum && (
                <>
                  {galleryLoading && (
                    <HStack spacing={2} justify="center" py={8}>
                      <Spinner size="lg" color="purple.500" />
                      <Text color="gray.600">Načítám alba z galerie...</Text>
                    </HStack>
                  )}
                  {!galleryLoading && cachedAlbums.length > 0 && (
                    <VStack align="stretch" spacing={6}>
                      {cachedAlbums.map((album) => (
                        <Box 
                          key={album.id} 
                          borderWidth="1px" 
                          borderRadius="md" 
                          p={4} 
                          bg={albumCardBg}
                          cursor="pointer"
                          onClick={() => { setExistingSelectedAlbum(album); setExistingSelectedPhotos(new Set()); setExistingVisibleCount(60); }}
                          transition="all 0.2s"
                          _hover={{ transform: 'scale(1.02)', boxShadow: 'md' }}
                        >
                          <HStack justify="space-between" mb={3}>
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="bold" fontSize="lg">{album.title || 'Album bez názvu'}</Text>
                              <Text fontSize="sm" color="gray.500">{album.date} • {(album.photos?.length || 0)} fotografií</Text>
                            </VStack>
                            <Icon as={FiExternalLink} color="purple.500" boxSize={5} />
                          </HStack>
                          <SimpleGrid columns={{ base: 6, md: 10 }} spacing={2}>
                            {(album.photos || []).slice(0, 20).map((photo) => (
                              <AspectRatio key={photo.id} ratio={1}>
                                <Image src={photo.image_1500} alt={photo.id} objectFit="cover" loading="lazy" decoding="async" />
                              </AspectRatio>
                            ))}
                          </SimpleGrid>
                        </Box>
                      ))}
                    </VStack>
                  )}
                  {!galleryLoading && cachedAlbums.length === 0 && (
                    <VStack py={8} spacing={3}>
                      <Icon as={FiSearch} boxSize={12} color="gray.400" />
                      <Text color="gray.600" textAlign="center">Žádná alba nebyla nalezena v cache.</Text>
                      <Button size="sm" onClick={fetchCachedGallery} leftIcon={<FiRefreshCcw />}>Obnovit seznam</Button>
                    </VStack>
                  )}
                </>
              )}

              {existingSelectedAlbum && (
                <VStack align="stretch" spacing={3}>
                  <HStack justify="space-between">
                    <Button size="sm" variant="ghost" onClick={() => { setExistingSelectedAlbum(null); setExistingSelectedPhotos(new Set()); setExistingVisibleCount(60); }}>← Zpět na seznam alb</Button>
                    <Checkbox
                      isChecked={existingSelectedAlbum ? existingSelectedPhotos.size === (existingSelectedAlbum.photos?.length || 0) : false}
                      isIndeterminate={existingSelectedAlbum ? (existingSelectedPhotos.size > 0 && existingSelectedPhotos.size < (existingSelectedAlbum.photos?.length || 0)) : false}
                      onChange={() => {
                        if (!existingSelectedAlbum) return;
                        if (existingSelectedPhotos.size === (existingSelectedAlbum.photos?.length || 0)) {
                          setExistingSelectedPhotos(new Set());
                        } else {
                          setExistingSelectedPhotos(new Set((existingSelectedAlbum.photos || []).map(p => p.id)));
                        }
                      }}
                    >
                      Vybrat vše ({existingSelectedPhotos.size}/{existingSelectedAlbum ? (existingSelectedAlbum.photos?.length || 0) : 0})
                    </Checkbox>
                  </HStack>
                  <SimpleGrid columns={{ base: 3, md: 4, lg: 5 }} spacing={3}>
                    {(existingSelectedAlbum?.photos || []).slice(0, existingVisibleCount).map((photo) => {
                      const checked = existingSelectedPhotos.has(photo.id);
                      return (
                        <Box
                          key={photo.id}
                          position="relative"
                          cursor="pointer"
                          onClick={() => {
                            const next = new Set(existingSelectedPhotos);
                            if (next.has(photo.id)) next.delete(photo.id); else next.add(photo.id);
                            setExistingSelectedPhotos(next);
                          }}
                          borderRadius="md"
                          overflow="hidden"
                          borderWidth="2px"
                          borderColor={checked ? 'purple.500' : 'transparent'}
                          transition="all 0.2s"
                          _hover={{ transform: 'scale(1.05)' }}
                        >
                          <Image src={photo.image_1500} alt={photo.id} w="100%" h="150px" objectFit="cover" loading="lazy" decoding="async" />
                          <Checkbox position="absolute" top={2} right={2} isChecked={checked} pointerEvents="none" bg="white" borderRadius="sm" />
                        </Box>
                      );
                    })}
                  </SimpleGrid>
                  {(existingSelectedAlbum?.photos?.length || 0) > existingVisibleCount && (
                    <HStack justify="center" pt={2}>
                      <Button size="sm" onClick={() => setExistingVisibleCount(c => c + 60)}>Načíst další</Button>
                    </HStack>
                  )}
                </VStack>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={() => { setExistingSelectedAlbum(null); setExistingSelectedPhotos(new Set()); onExistingAlbumsClose(); }}>Zrušit</Button>
              <Button
                colorScheme="purple"
                onClick={() => {
                  if (!existingSelectedAlbum || existingSelectedPhotos.size === 0) return;
                  const photos = (existingSelectedAlbum.photos || []).filter(p => existingSelectedPhotos.has(p.id));
                  handleAlbumPhotosSelected(photos as any, {
                    id: existingSelectedAlbum.id,
                    title: existingSelectedAlbum.title || '',
                    url: '', // already cached; skip saveAlbumToCache
                    date: existingSelectedAlbum.date,
                    photos_count: (existingSelectedAlbum.photos?.length || 0),
                    photos: (existingSelectedAlbum.photos || []),
                  });
                  setExistingSelectedAlbum(null);
                  setExistingSelectedPhotos(new Set());
                  onExistingAlbumsClose();
                }}
                isDisabled={!existingSelectedAlbum || existingSelectedPhotos.size === 0}
              >
                Vložit vybrané ({existingSelectedPhotos.size || 0})
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* YouTube Video Picker Modal */}
      <Modal isOpen={isYouTubeModalOpen} onClose={onYouTubeModalClose} size="6xl">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>Vybrat video z YouTube kanálu</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <VStack align="stretch" spacing={4}>
              {/* Search Input */}
              <FormControl>
                <FormLabel>Vyhledat video</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FiSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Název videa nebo ID..."
                    value={youtubeSearch}
                    onChange={(e) => setYoutubeSearch(e.target.value)}
                  />
                </InputGroup>
              </FormControl>

              {/* Loading State */}
              {youtubeLoading && (
                <HStack spacing={2} justify="center" py={8}>
                  <Spinner size="lg" color="blue.500" />
                  <Text color="gray.600">Načítám videa z kanálu...</Text>
                </HStack>
              )}

              {/* Videos Grid */}
              {!youtubeLoading && filteredYoutubeVideos.length > 0 && (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  {filteredYoutubeVideos.map((video) => (
                    <Box
                      key={video.video_id}
                      borderWidth="1px"
                      borderRadius="md"
                      overflow="hidden"
                      cursor="pointer"
                      transition="all 0.2s"
                      _hover={{ boxShadow: 'lg', transform: 'scale(1.02)' }}
                      onClick={() => handleSelectYoutubeVideo(video)}
                    >
                      <AspectRatio ratio={16 / 9}>
                        <Image
                          src={video.thumbnail_url}
                          alt={video.title}
                          objectFit="cover"
                        />
                      </AspectRatio>
                      <Box p={3}>
                        <Text fontWeight="semibold" fontSize="sm" noOfLines={2}>
                          {video.title}
                        </Text>
                        {video.published_date && (
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            {new Date(video.published_date).toLocaleDateString('cs-CZ')}
                          </Text>
                        )}
                      </Box>
                    </Box>
                  ))}
                </SimpleGrid>
              )}

              {/* Empty State */}
              {!youtubeLoading && filteredYoutubeVideos.length === 0 && youtubeVideos.length === 0 && (
                <VStack py={8} spacing={3}>
                  <Icon as={FiVideo} boxSize={12} color="gray.400" />
                  <Text color="gray.600" textAlign="center">
                    Žádná videa nebyla nalezena.
                  </Text>
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    Zkontrolujte nastavení YouTube kanálu v administraci.
                  </Text>
                  <Button size="sm" onClick={fetchYouTubeVideos} leftIcon={<FiRefreshCcw />}>
                    Obnovit seznam
                  </Button>
                </VStack>
              )}

              {/* No Results for Search */}
              {!youtubeLoading && filteredYoutubeVideos.length === 0 && youtubeVideos.length > 0 && (
                <VStack py={8}>
                  <Text color="gray.600">Žádná videa neodpovídají vašemu hledání.</Text>
                  <Button size="sm" variant="link" onClick={() => setYoutubeSearch('')}>
                    Zobrazit všechna videa
                  </Button>
                </VStack>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onYouTubeModalClose}>
              Zavřít
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Zonerama Gallery Picker Modal */}
      <Modal isOpen={isGalleryPickerOpen} onClose={onGalleryPickerClose} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>Vybrat fotku z galerie</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <VStack align="stretch" spacing={4}>
              {/* Loading State */}
              {galleryLoading && (
                <HStack spacing={2} justify="center" py={8}>
                  <Spinner size="lg" color="purple.500" />
                  <Text color="gray.600">Načítám alba z galerie...</Text>
                </HStack>
              )}

              {/* Albums Grid */}
              {!galleryLoading && cachedAlbums.length > 0 && (
                <VStack align="stretch" spacing={6}>
                  {cachedAlbums.map((album) => {
                    const isExpanded = galleryExpandedAlbumId === album.id;
                    const photos = album.photos || [];
                    return (
                      <Box key={album.id} borderWidth="1px" borderRadius="md" p={4} bg={albumCardBg}>
                        <HStack justify="space-between" mb={3}>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="bold" fontSize="lg">{album.title || 'Album bez názvu'}</Text>
                            <Text fontSize="sm" color="gray.500">{album.date} • {photos.length} fotografií</Text>
                          </VStack>
                          <HStack spacing={2}>
                            {photos.length > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  if (isExpanded) {
                                    setGalleryExpandedAlbumId(null);
                                  } else {
                                    setGalleryExpandedAlbumId(album.id);
                                    setGalleryVisibleCount(48);
                                  }
                                }}
                              >
                                {isExpanded ? 'Skrýt fotky' : 'Zobrazit fotky'}
                              </Button>
                            )}
                            {(album as any).url && (
                              <Button
                                as="a"
                                size="sm"
                                href={(album as any).url}
                                target="_blank"
                                rel="noopener noreferrer"
                                leftIcon={<FiExternalLink />}
                                variant="ghost"
                              >
                                Otevřít album
                              </Button>
                            )}
                          </HStack>
                        </HStack>

                        {isExpanded && photos.length > 0 && (
                          <>
                            <SimpleGrid columns={{ base: 3, md: 4, lg: 6 }} spacing={2}>
                              {photos.slice(0, galleryVisibleCount).map((photo) => (
                                <Box
                                  key={photo.id}
                                  borderWidth="1px"
                                  borderRadius="md"
                                  overflow="hidden"
                                  cursor="pointer"
                                  transition="all 0.2s"
                                  _hover={{ boxShadow: 'lg', transform: 'scale(1.05)' }}
                                  onClick={() => {
                                    pickZoneramaImage({
                                      id: photo.id,
                                      album_id: album.id,
                                      album_url: (album as any).url || `https://eu.zonerama.com/Album/${album.id}`,
                                      page_url: photo.page_url,
                                      image_url: photo.image_1500,
                                      title: album.title
                                    });
                                    onGalleryPickerClose();
                                  }}
                                >
                                  <AspectRatio ratio={1}>
                                    <Image
                                      src={photo.image_1500}
                                      alt={photo.id}
                                      objectFit="cover"
                                      loading="lazy"
                                      decoding="async"
                                    />
                                  </AspectRatio>
                                </Box>
                              ))}
                            </SimpleGrid>
                            {photos.length > galleryVisibleCount && (
                              <HStack justify="center" mt={2}>
                                <Button size="sm" onClick={() => setGalleryVisibleCount((c) => c + 48)}>Načíst další</Button>
                              </HStack>
                            )}
                          </>
                        )}
                      </Box>
                    );
                  })}
                </VStack>
              )}

              {/* Empty State */}
              {!galleryLoading && cachedAlbums.length === 0 && (
                <VStack py={8} spacing={3}>
                  <Icon as={FiSearch} boxSize={12} color="gray.400" />
                  <Text color="gray.600" textAlign="center">
                    Žádná alba nebyla nalezena v cache.
                  </Text>
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    Zkontrolujte nastavení Zonerama nebo obnovte cache.
                  </Text>
                  <Button size="sm" onClick={fetchCachedGallery} leftIcon={<FiRefreshCcw />}>
                    Obnovit seznam
                  </Button>
                </VStack>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onGalleryPickerClose}>
              Zavřít
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      
      <AILoadingModal
        isOpen={aiMut.isLoading}
        onClose={() => {}} // Can't close while AI is working
        title="AI generuje článek"
        message="AI pracuje na rozvinutí vašeho textu do plnohodnotného článku..."
        progress={aiProgress}
        estimatedTime={aiStartTime ? 25 : undefined} // 25 seconds estimated
      />
    </Box>
    </AdminLayout>
  );
};

export default ArticlesAdminPage;
